import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  InlineGrid,
  Badge,
  Spinner,
  Banner,
  Button,
  Popover,
  ActionList,
  Tabs,
  Modal,
  FormLayout,
  TextField,
  Select,
  DataTable,
  EmptyState,
  Icon,
  ProgressBar,
  Grid,
  Checkbox,
  IndexTable,
  useIndexResourceState,
  Filters,
  ChoiceList,
  RangeSlider,
  Divider,
  DatePicker,
  Tooltip,
  Pagination
} from '@shopify/polaris';
import {
  StoreIcon,
  CheckCircleIcon,
  PlusIcon,
  RefreshIcon,
  ViewIcon,
  ExternalIcon,
  CashDollarIcon,
  OrderIcon,
  ReceiptRefundIcon,
  ReturnIcon
} from '@shopify/polaris-icons';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../utils/api';
import { useBrandAnalytics, useBrandOrders, useMarketplacesList } from '../utils/queries';
import MetricCard, { RevenueCard, CommissionCard, OrdersCard } from '../components/Analytics/MetricCard';
import MarketplaceBreakdown from '../components/Analytics/MarketplaceBreakdown';
import LogisticsBreakdown from '../components/Analytics/LogisticsBreakdown';
import OrderDetailPanel from '../components/OrderDetailPanel';
import { validateNumeric } from '../utils/inputSecurity';
import { ORDER_STATUS_OPTIONS } from '../utils/dataValidation';
import { DEFAULT_PAGE_SIZE, PageSizeSelect } from '../utils/table';
import ShippingPricingTable from '../components/ShippingPricingTable';
import TwShippingLocations from '../components/TwShippingLocations';
import StorageFeeSettings from '../components/StorageFeeSettings';
import {
  SectionBody, SectionToggle, returnFeeSummary, shippingSummary, useSectionOpen,
} from '../components/CollapsibleSection';
import CommissionAdjustmentsTable from '../components/CommissionAdjustments/CommissionAdjustmentsTable';
import { CommissionSettings } from '../components/CommissionSettings';
import { InvoiceSettings } from '../components/InvoiceSettings';
import { OrderSourceSettings } from '../components/OrderSourceSettings';
import ConnectorTab from '../components/ConnectorTab';
import ShopifyCatalogueTab from '../components/ShopifyCatalogueTab';
import QueueTable from '../components/QueueTable';
import SyncHistoryTable from '../components/SyncHistoryTable';

/**
 * The tab list, and the ONLY place tab order is defined.
 *
 * Tab positions used to be hardcoded integers in five separate places — the ?tab= switch,
 * two effects keyed on `selectedTab === 2` / `=== 4`, and the render chain. Inserting a tab
 * meant renumbering all of them by hand, and missing one fails silently: the wrong panel
 * renders, or a fetch effect stops firing, with no error. Everything now resolves through
 * tabIndex(id) against this array.
 *
 * INVARIANT: any conditionally-rendered tab must stay LAST. Polaris Tabs reports the index
 * within the array it was handed, so a hidden tab in the middle would shift every index
 * after it out of step with the positions computed here. `connector` is the only conditional
 * one today, and it is last.
 */
/**
 * Analytics period presets, module-level so the button label and the menu are built from one
 * list. When they were two literals in the JSX, the label had to re-derive what the menu had
 * just set.
 */
const PERIOD_PRESETS = [
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'This Month to Date', value: 'thisMonth' },
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'Last 365 days', value: '365' },
];

const BRAND_TABS = [
  { id: 'settings', content: 'Settings', accessibilityLabel: 'Configure brand settings' },
  { id: 'shipping', content: 'Shipping & Return', accessibilityLabel: 'Configure shipping and return fees' },
  { id: 'catalogue', content: 'Catalogue', accessibilityLabel: 'Products and variants mirrored from Shopify' },
  { id: 'analytics', content: 'Analytics', accessibilityLabel: 'View brand performance analytics' },
  { id: 'orders', content: 'Orders', accessibilityLabel: 'Brand orders, and syncing or recalculating them' },
  { id: 'adjustments', content: 'Commission Adjustments', accessibilityLabel: 'Manage commission adjustments' },
  { id: 'activity', content: 'Activity Log', accessibilityLabel: 'View configuration changes' },
  // Unconditional, and therefore before the conditional tab below rather than after it.
  //
  // They could not be gated on connectorEnabled even if the ordering allowed it: both show
  // BILLING runs as well as connector ones, and a brand has billing runs whether or not it
  // is connected to a retailer. Hiding them with Retail Connect would have hidden the 51
  // billing runs this brand already has — 19 of them failed — behind a switch that has
  // nothing to do with billing.
  { id: 'queue', content: 'Queue', accessibilityLabel: 'Background work queued or running for this brand' },
  { id: 'history', content: 'Sync History', accessibilityLabel: 'The permanent record of background runs for this brand' },
  // Conditional — must remain last. See the invariant above.
  { id: 'connector', content: 'Retail Connect', accessibilityLabel: 'Retailer connections and listing status', requiresConnector: true },
];

/**
 * The brand Orders table's columns — ONE definition, with the headings, the sortable flags,
 * the toggle labels and the API sort field all derived from it.
 *
 * WHY. This was three lists indexed by position: `ordersHeadings` (15 entries), a `sortFields`
 * array inside handleOrdersSort (12), and a `sortable` literal marking indices 0-13. Because
 * the field list was three short, nine headings sorted by the wrong column — Status by
 * totalWeight, Weight by totalItems, Items by grossAmount, Sales by refundAmount, Refunds by
 * netSales, Net Sales by appliedCommissionRate, Marketplace Commission by appliedShippingFee,
 * 2W Commission by appliedReturnFee, Shipping Fees by payout — and Return Fee and Net Payout
 * sorted by `undefined`, which cleared the sort and put the arrow back on column 0. A
 * hand-maintained parallel array is what failed; there is only one array now.
 *
 * `sortField` is the name GET /orders/:brandId accepts, taken from that endpoint's own
 * whitelist (2w-connect web/modules/admin/billing/controllers/orders.controller.js,
 * sortFieldMapping). A name outside it is mapped to `order_date`, so it is not a sort at all —
 * just a silent one.
 *
 * A column with NO `sortField` is NOT sortable, because the endpoint has no field for it:
 * `financialStatus` (Status) and `totalItems` (Items) are selected by the query but absent
 * from its whitelist, and neither commission column has a field of its own — the whitelisted
 * `appliedCommissionAmount` holds the SUM of the marketplace and 2W shares
 * (unified-sync-service.js), so it orders by neither of the two figures shown. The former
 * sortColumnIndex map here had already drifted the other way: it named
 * `appliedMarketplaceCommissionRate` and `applied2wCommissionRate`, which the sort handler
 * could never set and the server does not accept either.
 *
 * `sortWords` becomes sortToggleLabels, REQUIRED for these tooltips to render: on a sortable
 * column Polaris reads sortToggleLabels[index][direction] unguarded (IndexTable.js:477) and,
 * with the prop absent, returns the bare sort button and drops tooltipContent silently.
 * Derived here, so the map is complete for every sortable index and empty for the rest by
 * construction.
 */
export const BRAND_ORDER_COLUMNS = [
  { title: 'Order', tip: 'The Shopify order number.', sortField: 'orderNumber', sortWords: { ascending: 'A-Z', descending: 'Z-A' } },
  { title: 'Date', tip: 'When the order was placed.', sortField: 'orderDate', sortWords: { ascending: 'Oldest first', descending: 'Newest first' } },
  { title: 'Retailer', tip: 'Which retailer the order was attributed to, resolved from its tag. An order matching no pattern falls to Others at the wrong rate.', sortField: 'marketplace', sortWords: { ascending: 'A-Z', descending: 'Z-A' } },
  { title: 'Source', tip: 'The Shopify app or channel the order came in through. The retailer beside it is resolved from the order’s tag; this is where the order actually entered the store.' },
  { title: 'Status', tip: 'The order’s financial status in Shopify.' },
  { title: 'Weight', tip: 'Total shipment weight, which is what the shipping fee is priced on.', sortField: 'totalWeight', sortWords: { ascending: 'Lightest first', descending: 'Heaviest first' } },
  { title: 'Items', tip: 'Units on the order.' },
  { title: 'Sales', tip: 'Gross sales less discounts.', sortField: 'grossAmount', sortWords: { ascending: 'Lowest first', descending: 'Highest first' } },
  { title: 'Refunds', tip: 'Refunded amount against this order.', sortField: 'refundAmount', sortWords: { ascending: 'Lowest first', descending: 'Highest first' } },
  { title: 'Net Sales', tip: 'Sales less refunds — the base every commission below is calculated on.', sortField: 'netAmount', sortWords: { ascending: 'Lowest first', descending: 'Highest first' } },
  { title: 'Marketplace Commission', tip: '2W’s own calculation: the configured marketplace rate applied to net sales. It is not the retailer’s reported deduction.' },
  { title: '2W Commission', tip: '2W’s own share at the configured rate.' },
  { title: 'Shipped by', tip: 'Who shipped the order, from its fulfilment locations against the ones ticked on Shipping & Return. An order the brand shipped itself is charged no shipping fee, so this is what tells a $0.00 beside it from one nobody has priced yet.', sortField: 'shippedBy', sortWords: { ascending: 'Brand first', descending: '2W first' } },
  { title: 'Shipping Fees', tip: 'What 2W charged for shipping, from this brand’s rate table. Not a carrier cost.', sortField: 'appliedShippingFee', sortWords: { ascending: 'Lowest first', descending: 'Highest first' } },
  { title: 'Return Fee', tip: 'What 2W charged for a return on this order.', sortField: 'appliedReturnFee', sortWords: { ascending: 'Lowest first', descending: 'Highest first' } },
  { title: 'Net Payout', tip: 'Net sales less every fee above. 2W’s arithmetic, not a figure any retailer reported.', sortField: 'payout', sortWords: { ascending: 'Lowest first', descending: 'Highest first' } },
  { title: 'Actions', alignment: 'end', tip: 'Open or adjust this order.' },
];

/** Headings. Tips inline rather than through headingsFor, which builds IndexTable ids too. */
export const BRAND_ORDER_HEADINGS = BRAND_ORDER_COLUMNS.map(({ title, tip, alignment }) => ({
  title,
  alignment: alignment || 'start',
  ...(tip ? { tooltipContent: tip, tooltipWidth: 'wide' } : {}),
}));

/** Sortable exactly where a server field exists. */
export const BRAND_ORDER_SORTABLE = BRAND_ORDER_COLUMNS.map((c) => Boolean(c.sortField));

export const BRAND_ORDER_SORT_TOGGLE_LABELS = Object.fromEntries(
  BRAND_ORDER_COLUMNS
    .map((c, i) => [i, c.sortField ? c.sortWords : null])
    .filter(([, words]) => Boolean(words)),
);

/** Which heading carries the arrow, by field rather than by a substring test on the title. */
export const brandOrderSortColumnIndex = (sortBy) => {
  const index = BRAND_ORDER_COLUMNS.findIndex((c) => c.sortField === sortBy);
  return index === -1 ? 0 : index;
};

/**
 * Render any activity-log value as text.
 *
 * Exists because `${value}` on an object produces "[object Object]" — which is exactly what
 * the Activity Log showed for a commission adjustment update, whose payload nests a `changes`
 * object. Recursive, so a nested object is expanded rather than swallowed at any depth.
 */
const formatActivityValue = (value) => {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.map(formatActivityValue).join(', ');
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([k, v]) => `${k}: ${formatActivityValue(v)}`)
      .join(', ');
  }
  return String(value);
};

/** Position of a tab by id. Falls back to the first tab for an unknown id. */
const tabIndex = (id) => {
  const i = BRAND_TABS.findIndex((t) => t.id === id);
  return i === -1 ? 0 : i;
};

export default function BrandConfiguration() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Helper function to format activity details with robust JSON parsing
  const formatActivityDetails = (action, details) => {
    // Safely parse details if it's a JSON string
    let parsedDetails = details;
    if (typeof details === 'string' && details.trim()) {
      try {
        parsedDetails = JSON.parse(details);
      } catch (parseError) {
        // Only log parsing warnings in development environment
        if (process.env.NODE_ENV === 'development') {
          console.warn('BrandConfiguration: Failed to parse activity details JSON:', {
            action,
            details,
            error: parseError.message
          });
        }
        return details; // Return original string if parsing fails
      }
    }

    // Format based on action type
    switch (action.toLowerCase()) {
      case 'updated default commission rate':
      case 'commission_rate_updated':
        return `Changed from ${(parsedDetails.oldRate * 100).toFixed(1)}% to ${(parsedDetails.newRate * 100).toFixed(1)}%`;

      case 'added commission override':
      case 'commission_override_added':
        return `${parsedDetails.orderTag}: ${(parsedDetails.commissionRate * 100).toFixed(1)}%`;

      case 'removed commission override':
      case 'commission_override_deleted':
        return `Removed override for ${parsedDetails.orderTag}`;

      case 'added shipping fee rule':
      case 'shipping_fee_added':
        return `${parsedDetails.shippingMethod} (${parsedDetails.weightMin}-${parsedDetails.weightMax} lbs): $${parsedDetails.fee.toFixed(2)}`;

      case 'removed shipping fee rule':
      case 'shipping_fee_deleted':
        return `Removed ${parsedDetails.shippingMethod} fee rule`;

      case 'shipping_fees_reset_to_default':
        return `Reset ${parsedDetails.deletedCount || 0} shipping fee override(s) to global defaults`;

      case 'activated brand':
      case 'brand_activated':
        return `Brand status changed from ${parsedDetails.previousStatus ? 'Active' : 'Inactive'} to ${parsedDetails.newStatus ? 'Active' : 'Inactive'}`;

      case 'deactivated brand':
      case 'brand_deactivated':
        return `Brand status changed from ${parsedDetails.previousStatus ? 'Active' : 'Inactive'} to ${parsedDetails.newStatus ? 'Active' : 'Inactive'}`;

      case 'shipping_pricing_updated':
      case 'bulk_shipping_pricing_updated':
        if (parsedDetails.changes && Array.isArray(parsedDetails.changes)) {
          return parsedDetails.changes.map(change => {
            const oldPrice = change.oldFee != null ? `$${Number(change.oldFee).toFixed(2)}` : '—';
            const newPrice = change.newFee != null ? `$${Number(change.newFee).toFixed(2)}` : '—';
            return `${change.service} ${change.weightRange}: ${oldPrice} → ${newPrice}`;
          }).join('; ');
        }
        // Fallback for updates without detailed changes
        const summary = [];
        if (parsedDetails.updated > 0) summary.push(`${parsedDetails.updated} updated`);
        if (parsedDetails.created > 0) summary.push(`${parsedDetails.created} created`);
        if (parsedDetails.deleted > 0) summary.push(`${parsedDetails.deleted} deleted`);
        return summary.join(', ') || 'No changes';

      default:
        // For unknown actions, try to show meaningful info
        if (parsedDetails && typeof parsedDetails === 'object') {
          if (parsedDetails.previousStatus !== undefined && parsedDetails.newStatus !== undefined) {
            return `Status changed from ${parsedDetails.previousStatus ? 'Active' : 'Inactive'} to ${parsedDetails.newStatus ? 'Active' : 'Inactive'}`;
          }
          // Show key-value pairs for other objects.
          //
          // Values are formatted recursively. `${value}` on an object yields the literal
          // string "[object Object]", which is what an admin was shown for any action with a
          // nested payload — and every future action with one would have done the same, so
          // this is fixed here rather than only in the cases added above.
          //
          // Internal ids are dropped: a cuid tells an admin nothing they can act on, and the
          // log row already identifies what changed. If an entry has nothing but ids, the
          // filter is skipped rather than rendering an empty cell.
          const HIDDEN = new Set(['adminUserEmail']);
          const isNoisyId = (key) => /(^id$|Id$)/.test(key);
          const entries = Object.entries(parsedDetails).filter(([key]) => !HIDDEN.has(key));
          const useful = entries.filter(([key]) => !isNoisyId(key));
          return (useful.length ? useful : entries)
            .map(([key, value]) => `${key}: ${formatActivityValue(value)}`)
            .join(', ');
        }
        return details; // Return original if can't format
    }
  };

  // Core state
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Initialize tab based on URL parameters
  const getInitialTab = () => tabIndex(searchParams.get('tab') || 'settings');

  const [selectedTab, setSelectedTab] = useState(getInitialTab());
  const [analyticsFilter, setAnalyticsFilter] = useState('30'); // days

  // Analytics date range state
  const [dateRangeMode, setDateRangeMode] = useState('preset'); // 'preset' | 'custom' | 'lastMonth' | 'thisMonth'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [periodPopoverActive, setPeriodPopoverActive] = useState(false);

  /** What the period button reads when the menu is shut. */
  const periodLabel = dateRangeMode === 'custom' && customStartDate && customEndDate
    ? `${customStartDate} to ${customEndDate}`
    : (PERIOD_PRESETS.find((o) => (
      dateRangeMode === 'preset' ? o.value === analyticsFilter : o.value === dateRangeMode
    ))?.label ?? 'Select period');

  /**
   * Seed the date picker before showing it. Kept out of the menu item so the two callers —
   * a fresh pick and changing an existing range — cannot drift apart.
   */
  const openCustomDatePicker = useCallback(() => {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      setSelectedDates({ start, end });
      setDatePickerMonth(start.getMonth());
      setDatePickerYear(start.getFullYear());
    } else {
      const today = new Date();
      setSelectedDates({ start: today, end: today });
      setDatePickerMonth(today.getMonth());
      setDatePickerYear(today.getFullYear());
    }
    setShowCustomDateModal(true);
  }, [customStartDate, customEndDate]);

  // DatePicker state - uses Date objects and {month, year} format
  const [selectedDates, setSelectedDates] = useState({
    start: new Date(),
    end: new Date()
  });
  const [datePickerMonth, setDatePickerMonth] = useState(new Date().getMonth());
  const [datePickerYear, setDatePickerYear] = useState(new Date().getFullYear());

  // Commission state.
  // Starts EMPTY, not '30'. This value is written to Brand.defaultCommissionRate by
  // updateDefaultCommission, so seeding it with a hardcoded 30 meant the form displayed - and a
  // stray Save would persist - a rate the brand never agreed to, for the whole window before
  // fetchBrandData overwrites it below. validateNumeric rejects '' so Save is refused until the
  // real rate has loaded.
  const [defaultRate, setDefaultRate] = useState('');



  // Return fees state
  const [defaultReturnFee, setDefaultReturnFee] = useState('0.00');
  const [hasDefaultReturnFee, setHasDefaultReturnFee] = useState(false);
  const [marketplaceReturnFees, setMarketplaceReturnFees] = useState({});
  const [showReturnFeeModal, setShowReturnFeeModal] = useState(false);
  const [editingMarketplace, setEditingMarketplace] = useState('');
  const [newReturnFee, setNewReturnFee] = useState('0.00');
  // Global return fee defaults and override tracking
  const [globalReturnFeeDefaults, setGlobalReturnFeeDefaults] = useState({ default: 0, hasGlobalDefault: false, marketplaces: {} });
  // True when the return-fee load failed, so the UI can say the default in force is unknown
  // instead of printing $0.00 as though it were the configured fee.
  const [returnFeesUnavailable, setReturnFeesUnavailable] = useState(false);
  const [returnFeeOverrideKeys, setReturnFeeOverrideKeys] = useState([]);
  // Reset to default modals
  const [showResetDefaultFeeModal, setShowResetDefaultFeeModal] = useState(false);
  const [showResetMarketplaceFeesModal, setShowResetMarketplaceFeesModal] = useState(false);
  const [resettingFees, setResettingFees] = useState(false);

  // Shipping enable/disable state
  const [shippingEnabled, setShippingEnabled] = useState(true);
  // Held as the string the admin typed rather than a number. '' is a real answer here -
  // it clears the fallback - and coercing early makes '' and 0 indistinguishable.
  const [fallbackFee, setFallbackFee] = useState('');
  const [savedFallbackFee, setSavedFallbackFee] = useState('');
  // Shipping & Return sections start closed; Shipping Configuration opens by itself when
  // shipping is on with no 2W location ticked, since nothing can then be charged.
  const shippingLocationCount = (brand?.twShippingLocationIds || []).length;
  const [shippingOpen, toggleShipping] = useSectionOpen(Boolean(brand) && shippingEnabled && shippingLocationCount === 0);
  const [returnFeeOpen, toggleReturnFee] = useSectionOpen(false);

  const [newRecipientEmail, setNewRecipientEmail] = useState('');

  // Orders state management (based on OrderManagement.jsx)
  const [ordersSearchQuery, setOrdersSearchQuery] = useState('');
  const [debouncedOrdersSearch, setDebouncedOrdersSearch] = useState('');
  const [ordersSortBy, setOrdersSortBy] = useState('orderDate');
  const [ordersSortDirection, setOrdersSortDirection] = useState('descending');
  const [ordersCurrentPage, setOrdersCurrentPage] = useState(1);

  // Resync state
  const [resyncingOrderId, setResyncingOrderId] = useState(null);
  const [resyncBanner, setResyncBanner] = useState(null);
  const resyncBannerTimeoutRef = useRef(null);

  /**
   * The brand's ENABLED retailer connections, for the retailer filter on the Queue and Sync
   * History tabs.
   *
   * Enabled only, and the brand's own: the rows on those tabs are already scoped to this
   * brand server-side, so offering every retailer in the system would fill the dropdown with
   * options that can only ever match nothing.
   *
   * Loaded when one of those tabs is first opened rather than with the page. Retail Connect
   * reads the same endpoint for its own purposes, and a brand page that fetched connections
   * on every visit would pay for them on the six tabs that do not use them.
   */
  const [runRetailerOptions, setRunRetailerOptions] = useState([]);
  const [runRetailersLoaded, setRunRetailersLoaded] = useState(false);

  // Filter states
  const [ordersStatusFilter, setOrdersStatusFilter] = useState([]);
  // Same filter as the global Orders page, for the same reason it is separate from
  // Status there: Status is what Shopify says happened, this is whether our own figure
  // is finished, and a brand's own tab is where someone chasing one brand would look.
  const [ordersShippingReviewFilter, setOrdersShippingReviewFilter] = useState([]);
  const [ordersMarketplaceFilter, setOrdersMarketplaceFilter] = useState([]);
  const [ordersAmountRange, setOrdersAmountRange] = useState([0, 5000]);

  // Date range filter states
  const [ordersStartDate, setOrdersStartDate] = useState('');
  const [ordersEndDate, setOrdersEndDate] = useState('');

  /**
   * Rows per page for the Orders table, seeded with the 10 this table has always opened on.
   *
   * It pages SERVER-side — the value goes out as the API's `limit` (see ordersQueryParams) and
   * the response's own pagination drives the pager — so the chosen size has to reach the
   * request. Slicing the response instead would show ten of the fifty rows the server sent and
   * label it "Page 1 of 5".
   */
  const [ordersItemsPerPage, setOrdersItemsPerPage] = useState(DEFAULT_PAGE_SIZE);

  // A new row count starts again at page 1: the old page number means nothing against a new
  // size, and on the last page it would ask the server for a page that no longer exists.
  const handleOrdersItemsPerPageChange = useCallback((next) => {
    setOrdersItemsPerPage(next);
    setOrdersCurrentPage(1);
  }, []);

  // Helper function to format date as YYYY-MM-DD without timezone shift
  // This avoids the bug where toISOString() converts to UTC, shifting dates by 1 day
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Date calculation helper functions
  const getLastMonthDates = useCallback(() => {
    const today = new Date();
    const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    return {
      startDate: formatDateLocal(firstDayLastMonth),
      endDate: formatDateLocal(lastDayLastMonth)
    };
  }, []);

  const getThisMonthToDateDates = useCallback(() => {
    const today = new Date();
    const firstDayThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      startDate: formatDateLocal(firstDayThisMonth),
      endDate: formatDateLocal(today)
    };
  }, []);

  // Get analytics parameters based on date range mode
  const getAnalyticsParams = useCallback(() => {
    switch (dateRangeMode) {
      case 'lastMonth': {
        const { startDate, endDate } = getLastMonthDates();
        return { startDate, endDate };
      }
      case 'thisMonth': {
        const { startDate, endDate } = getThisMonthToDateDates();
        return { startDate, endDate };
      }
      case 'custom':
        if (customStartDate && customEndDate) {
          return { startDate: customStartDate, endDate: customEndDate };
        }
        return { days: parseInt(analyticsFilter) };
      case 'preset':
      default:
        return { days: parseInt(analyticsFilter) };
    }
  }, [dateRangeMode, analyticsFilter, customStartDate, customEndDate, getLastMonthDates, getThisMonthToDateDates]);

  // Debounce search query for orders
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedOrdersSearch(ordersSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [ordersSearchQuery]);

  // Cleanup resync banner timeout on unmount
  useEffect(() => {
    return () => {
      if (resyncBannerTimeoutRef.current) {
        clearTimeout(resyncBannerTimeoutRef.current);
      }
    };
  }, []);

  // Build query parameters for orders API
  const ordersQueryParams = useMemo(() => ({
    page: ordersCurrentPage,
    limit: ordersItemsPerPage,
    sortBy: ordersSortBy,
    sortDirection: ordersSortDirection,
    search: debouncedOrdersSearch || undefined,
    statusFilter: ordersStatusFilter.length > 0 ? ordersStatusFilter : undefined,
    shippingFeeReview: ordersShippingReviewFilter.length > 0 ? 'true' : undefined,
    marketplace: ordersMarketplaceFilter.length > 0 ? ordersMarketplaceFilter : undefined,
    amountMin: ordersAmountRange[0] > 0 ? ordersAmountRange[0] : undefined,
    amountMax: ordersAmountRange[1] < 5000 ? ordersAmountRange[1] : undefined,
    startDate: ordersStartDate || undefined,
    endDate: ordersEndDate || undefined,
    // ordersItemsPerPage is a dependency now that it is state rather than a constant — without
    // it a size change would not rebuild the params and the request would not re-fire.
  }), [ordersCurrentPage, ordersItemsPerPage, ordersSortBy, ordersSortDirection, debouncedOrdersSearch, ordersStatusFilter, ordersShippingReviewFilter, ordersMarketplaceFilter, ordersAmountRange, ordersStartDate, ordersEndDate]);

  // Query hook for brand orders
  const {
    data: ordersData,
    isLoading: ordersLoading,
    error: ordersError,
    refetch: refetchOrders
  } = useBrandOrders(brandId, ordersQueryParams);

  // Fetch all marketplaces for this brand (independent of current page orders)
  const { data: marketplacesData, isLoading: marketplacesLoading } = useMarketplacesList(brandId);

  // Extract orders and pagination from response
  const allOrders = ordersData?.orders || [];

  // Separate summary row from regular orders
  const ordersSummaryRow = allOrders.find(order => order.isSummaryRow);
  const orders = allOrders.filter(order => !order.isSummaryRow);

  // The order behind the detail panel. Held as an id rather than the row, so a refetch
  // reopens on fresh figures instead of whatever was on screen when it was clicked.
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);

  const ordersPagination = ordersData?.pagination || {};
  const ordersTotalPages = ordersPagination.totalPages || 0;
  const ordersTotalOrders = ordersPagination.totalOrders || 0;

  // Get all marketplaces from dedicated API endpoint (not from current page orders)
  const ordersMarketplaceOptions = useMemo(() => {
    const marketplaces = marketplacesData?.marketplaces || [];
    return marketplaces.map(mp => ({
      label: mp,
      value: mp
    }));
  }, [marketplacesData]);

  const ordersResourceName = {
    singular: 'order',
    plural: 'orders',
  };


  // The orders table is `selectable={false}`, so `allResourcesSelected` and
  // `handleSelectionChange` could never affect anything - no bulk action can fire. Only the ids
  // are still read (row highlighting), so keep just those.
  const { selectedResources: ordersSelectedResources } = useIndexResourceState(orders);

  // Orders callback functions
  const handleOrdersFiltersClearAll = useCallback(() => {
    setOrdersSearchQuery('');
    setOrdersStatusFilter([]);
    setOrdersMarketplaceFilter([]);
    setOrdersAmountRange([0, 5000]);
    setOrdersStartDate('');
    setOrdersEndDate('');
    setOrdersCurrentPage(1);
  }, []);

  /**
   * Heading click -> API sort field, read off BRAND_ORDER_COLUMNS by the index Polaris gives.
   *
   * The list of field names that used to live here was three entries shorter than the
   * headings, so nine columns sorted by their neighbour's field and two sorted by
   * `undefined`. A column with no `sortField` is not marked sortable, so the guard should be
   * unreachable — it stays because writing `undefined` into `ordersSortBy` is exactly what
   * cleared the sort and reset the arrow to column 0.
   */
  const handleOrdersSort = useCallback((headingIndex, direction) => {
    const field = BRAND_ORDER_COLUMNS[headingIndex]?.sortField;
    if (!field) return;
    setOrdersSortBy(field);
    setOrdersSortDirection(direction);
    setOrdersCurrentPage(1);
  }, []);

  const handleOrdersPreviousPage = useCallback(() => {
    if (ordersCurrentPage > 1) {
      setOrdersCurrentPage(ordersCurrentPage - 1);
    }
  }, [ordersCurrentPage]);

  const handleOrdersNextPage = useCallback(() => {
    if (ordersCurrentPage < ordersTotalPages) {
      setOrdersCurrentPage(ordersCurrentPage + 1);
    }
  }, [ordersCurrentPage, ordersTotalPages]);

  // Resync single order handler
  const handleResyncOrder = useCallback(async (order) => {
    // Validate shopifyOrderId
    if (!order.shopifyOrderId) {
      setResyncBanner({
        tone: 'critical',
        message: 'Cannot resync: Order missing Shopify ID'
      });
      return;
    }

    try {
      setResyncingOrderId(order.id);
      setResyncBanner(null); // Clear any previous banners

      const response = await apiClient.request(
        `/orders/${order.shopifyOrderId}/resync`,
        {
          method: 'POST',
          body: JSON.stringify({ brandId }),
        }
      );

      if (response.success) {
        // Show success banner
        setResyncBanner({
          tone: 'success',
          message: `Order ${order.orderNumber} resynced successfully`
        });

        // Refetch orders to show updated data
        refetchOrders();

        // Clear any existing timeout
        if (resyncBannerTimeoutRef.current) {
          clearTimeout(resyncBannerTimeoutRef.current);
        }

        // Auto-dismiss success banner after 5 seconds
        resyncBannerTimeoutRef.current = setTimeout(() => {
          setResyncBanner(null);
          resyncBannerTimeoutRef.current = null;
        }, 5000);
      } else {
        throw new Error(response.error || 'Resync failed');
      }
    } catch (error) {
      console.error('Error resyncing order:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      setResyncBanner({
        tone: 'critical',
        message: `Failed to resync order: ${errorMsg}`
      });
    } finally {
      setResyncingOrderId(null);
    }
  }, [brandId, refetchOrders]);

  // Orders memoized data
  const ordersAppliedFilters = useMemo(() => {
    const filters = [];
    if (ordersSearchQuery) {
      filters.push({
        key: 'search',
        label: `Search: ${ordersSearchQuery}`,
        onRemove: () => setOrdersSearchQuery(''),
      });
    }
    if (ordersStatusFilter.length > 0) {
      filters.push({
        key: 'status',
        label: `Status: ${ordersStatusFilter.join(', ')}`,
        onRemove: () => setOrdersStatusFilter([]),
      });
    }
    if (ordersShippingReviewFilter.length > 0) {
      filters.push({
        key: 'shippingFeeReview',
        label: 'Shipping fee: needs review',
        onRemove: () => setOrdersShippingReviewFilter([]),
      });
    }
    if (ordersMarketplaceFilter.length > 0) {
      filters.push({
        key: 'marketplace',
        label: `Marketplace: ${ordersMarketplaceFilter.join(', ')}`,
        onRemove: () => setOrdersMarketplaceFilter([]),
      });
    }
    if (ordersAmountRange[0] > 0 || ordersAmountRange[1] < 5000) {
      filters.push({
        key: 'amount',
        label: `Amount: $${ordersAmountRange[0]} - $${ordersAmountRange[1]}`,
        onRemove: () => setOrdersAmountRange([0, 5000]),
      });
    }
    if (ordersStartDate || ordersEndDate) {
      const dateLabel = ordersStartDate && ordersEndDate
        ? `Date: ${ordersStartDate} to ${ordersEndDate}`
        : ordersStartDate
          ? `Date: From ${ordersStartDate}`
          : `Date: Until ${ordersEndDate}`;
      filters.push({
        key: 'dateRange',
        label: dateLabel,
        onRemove: () => {
          setOrdersStartDate('');
          setOrdersEndDate('');
        },
      });
    }
    return filters;
  }, [ordersSearchQuery, ordersStatusFilter, ordersShippingReviewFilter, ordersMarketplaceFilter, ordersAmountRange, ordersStartDate, ordersEndDate]);

  // Table rows - includes both orders and refund line items
  const ordersRowMarkup = useMemo(() => {
    const allRows = orders.flatMap((order, orderIndex) => {
      const rows = [];

      const getTone = (value) => {
        if (value < 0) return 'critical';
        if (value > 0) return 'success';
        return 'base';
      };

      // Add main order row
      rows.push(
        <IndexTable.Row
          id={order.id}
          key={order.id}
          selected={ordersSelectedResources.includes(order.id)}
          position={orderIndex}
          // A refund row keeps whatever it had; an order whose fee nobody finished is
          // tinted so it stands out while scanning. 'warning' is one of the four values
          // IndexTable.Row actually honours.
          tone={order.isRefundLineItem
            ? 'caution'
            : (order.shippingFeeNeedsReview ? 'warning' : undefined)}
        >
          {/* Order */}
          <IndexTable.Cell>
            <BlockStack gap="100">
              <Text variant="bodyMd" fontWeight="semibold" tone={order.isRefundLineItem ? 'caution' : undefined}>
                {/* The server already concatenates " - REFUND" onto a refund row's number
                    (orders.controller.js), so appending it here printed it twice. */}
                {order.orderNumber}
              </Text>
              {/* IN THE FIRST COLUMN ON PURPOSE. The row tone Polaris applies paints the
                  row element and only the first, second and last cells; every cell between
                  keeps its own opaque background and covers it. On a table this wide those
                  tinted edges are scrolled off, which is why the tint appears to show only
                  on hover - hover is the one state that paints every cell. This column is
                  always on screen. */}
              {order.shippingFeeNeedsReview && (
                <Badge tone="warning">Shipping fee needs review</Badge>
              )}
              {order.isRefundLineItem && (
                <Box maxWidth='200px'>
                  <Tooltip content={order.refundReason || 'Refund processed'}>
                    <Text variant="bodySm" tone="subdued">
                      {order?.refundReason ? (order.refundReason.length > 40 ? `${order.refundReason.slice(0, 40)}...` : order.refundReason) : 'Refund processed'}
                    </Text>
                  </Tooltip>
                </Box>
              )}
            </BlockStack>
          </IndexTable.Cell>

          {/* Date */}
          <IndexTable.Cell>
            <Text variant="bodyMd">
              {order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              }) : 'N/A'}
            </Text>
          </IndexTable.Cell>

          {/* Retailer */}
          <IndexTable.Cell>
            <Badge tone={order.isRefundLineItem ? 'caution' : 'info'}>
              {order.marketplace || 'Direct'}
            </Badge>
          </IndexTable.Cell>

          {/* Source — where the order entered the store, which is not the same question as
              the Retailer beside it: that is resolved from the order's tag, this is the app
              that created it. An order tagged for no retailer still has a source. */}
          <IndexTable.Cell>
            <Text variant="bodyMd" tone={order.appName ? undefined : 'subdued'}>
              {order.appName || '—'}
            </Text>
          </IndexTable.Cell>

          {/* Status — the wireframe carries it here and the table was missing it.
              A refund row states what it is; otherwise this is the order's billing
              status, the same field the admin Orders Management table shows. */}
          <IndexTable.Cell>
            {order.isRefundLineItem ? (
              <Badge tone="caution">Refunded</Badge>
            ) : (
              <Badge tone={
                order.financialStatus === 'PAID' ? 'success'
                  : order.financialStatus === 'REFUNDED' ? 'critical'
                    : order.financialStatus === 'PARTIALLY_REFUNDED' ? 'warning'
                      : undefined
              }>
                {String(order.financialStatus || order.fulfillmentStatus || 'unknown').toLowerCase()}
              </Badge>
            )}
          </IndexTable.Cell>

          {/* Weight */}
          <IndexTable.Cell>
            <Text variant="bodyMd">
              {order.totalWeight ? apiClient.formatWeight(order.totalWeight) : '-'}
            </Text>
          </IndexTable.Cell>

          {/* Items (NEW) */}
          <IndexTable.Cell>
            <Text variant="bodyMd">
              {order.totalItems || 0}
            </Text>
          </IndexTable.Cell>

          {/* Sales (renamed from Gross Revenue - value is gross, discounts not subtracted) */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="medium">
              {apiClient.formatCurrency(order.grossAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Refunds */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="medium" tone={order.isRefundLineItem && order.refundAmount ? 'critical' : undefined}>
              {apiClient.formatCurrency(order.refundAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Net Sales */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="medium" tone="success">
              {/* Prefer the API's netAmount - which is exactly what the TOTAL row of this same
                  table already uses (ordersSummaryRow.netAmount), so the column and its own
                  total were being produced by two different formulas and could disagree. The
                  Math.max(0, ...) clamp is gone too: it hid a legitimately negative net where a
                  refund exceeded the order. The local formula survives only as a fallback for a
                  payload with no netAmount, marked approximate when used. */}
              {(() => {
                const apiNet = parseFloat(order.netAmount);
                if (Number.isFinite(apiNet)) return apiClient.formatCurrency(apiNet);
                const grossAmount = Number(order.grossAmount) || 0;
                const discountAmount = Number(order.discountAmount) || 0;
                const refundAmount = Number(order.refundAmount) || 0;
                return `~${apiClient.formatCurrency(grossAmount - discountAmount - refundAmount)}`;
              })()}
            </Text>
          </IndexTable.Cell>


          {/* MKT Commission */}
          <IndexTable.Cell>
            <BlockStack gap="100">
              <Text tone={getTone(order.appliedMarketplaceCommissionAmount)} variant="bodyMd">
                {apiClient.formatCurrency(order.appliedMarketplaceCommissionAmount ?? 0)}
              </Text>
              <Text variant="bodySm" tone="subdued">
                {apiClient.formatPercentage(order.appliedMarketplaceCommissionRate || 0)}
              </Text>
            </BlockStack>
          </IndexTable.Cell>

          {/* 2W Commission */}
          <IndexTable.Cell>
            <BlockStack gap="100">
              <Text tone={getTone(order.applied2wCommissionAmount)} variant="bodyMd">
                {apiClient.formatCurrency(order.applied2wCommissionAmount ?? 0)}
              </Text>
              <Text variant="bodySm" tone="subdued">
                {apiClient.formatPercentage(order.applied2wCommissionRate ?? 0)}
              </Text>
            </BlockStack>
          </IndexTable.Cell>

          {/* Shipped by */}
          <IndexTable.Cell>
            <BlockStack gap="050">
              <Text variant="bodyMd">
                {order.shippedByLabel || <Text tone="subdued" as="span">&mdash;</Text>}
              </Text>
              {/* Console only. The brand-facing app deliberately never sees a warehouse
                  name, so this is where 2W staff can tell which location it left from. */}
              {order.fulfilledFrom ? (
                <Text variant="bodySm" tone="subdued">{order.fulfilledFrom}</Text>
              ) : null}
            </BlockStack>
          </IndexTable.Cell>

          {/* Shipping Fees */}
          <IndexTable.Cell>
            <BlockStack gap="050">
              <Text tone={getTone(order.appliedShippingFee || 0)} variant="bodyMd">
                {apiClient.formatCurrency(order.appliedShippingFee || 0)}
              </Text>
              {/* Only ever rendered for a fee nobody finished working out - the statuses
                  that are answers carry no label, so ordinary rows gain nothing. */}
              {order.shippingFeeStatusLabel && (
                <Text variant="bodySm" tone="caution">
                  {order.shippingFeeStatusLabel}
                </Text>
              )}
              {/* The stored fee and the invoice disagree until somebody acts on this, which
                  is the whole point of holding it - but only if the row says so. */}
              {order.shippingFeePendingFallback > 0 && (
                <Text variant="bodySm" tone="subdued">
                  {apiClient.formatCurrency(order.shippingFeePendingFallback)} fallback will apply
                </Text>
              )}
            </BlockStack>
          </IndexTable.Cell>

          {/* Return Fee */}
          <IndexTable.Cell>
            <Text tone={getTone(order.appliedReturnFee || 0)} variant="bodyMd">
              {apiClient.formatCurrency(order.appliedReturnFee || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Net Payout */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="medium" tone={order.isRefundLineItem && order.netAmount < 0 ? 'critical' : undefined}>
              {apiClient.formatCurrency(order.payout || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Actions — the two the global Orders Management table offers, plus the resync
              only a brand tab can do. wrap={false} because the column is narrow enough that
              a third icon otherwise drops onto its own line.

              Only the eye opens the panel; the row stays inert, as on the global table.
              stopPropagation is kept on all three so restoring row-click later cannot
              silently make "View in Shopify" open the panel as well. */}
          <IndexTable.Cell>
            <InlineStack align="end" gap="250" wrap={false} blockAlign="center">
              <Tooltip content="View order details">
                <Button
                  variant="tertiary"
                  size="micro"
                  icon={ViewIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOrderId(order.id);
                    setOrderDetailOpen(true);
                  }}
                  accessibilityLabel={`View details for order ${order.orderNumber}`}
                />
              </Tooltip>
              {brand?.shopDomain && order.shopifyOrderId && (
                <Tooltip content="View in Shopify">
                  <Button
                    variant="tertiary"
                    size="micro"
                    icon={ExternalIcon}
                    url={`https://${brand.shopDomain}/admin/orders/${order.shopifyOrderId}`}
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                    accessibilityLabel={`View order ${order.orderNumber} in Shopify`}
                  />
                </Tooltip>
              )}
              <Tooltip content="Resync this order">
                <Button
                  variant="tertiary"
                  size="micro"
                  icon={RefreshIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResyncOrder(order);
                  }}
                  loading={resyncingOrderId === order.id}
                  disabled={resyncingOrderId !== null || !order.shopifyOrderId}
                  accessibilityLabel={`Resync order ${order.orderNumber}`}
                />
              </Tooltip>
            </InlineStack>
          </IndexTable.Cell>
        </IndexTable.Row>
      );

      return rows;
    });

    // Add summary row at the end if it exists
    if (ordersSummaryRow) {
      const summaryRowMarkup = (
        <IndexTable.Row
          id="SUMMARY_ROW"
          key="SUMMARY_ROW"
          selected={false}
          position={allRows.length}
          tone="subdued"
        >
          {/* Order - TOTAL label */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              TOTAL
            </Text>
          </IndexTable.Cell>

          {/* Date - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Retailer - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Source - empty. Present for the same reason as Status below. */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Status - empty. Present so the totals row keeps the same cell count as a
              data row; without it every money column below shifts one place left. */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Weight */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {ordersSummaryRow.totalWeight ? apiClient.formatWeight(ordersSummaryRow.totalWeight) : '-'}
            </Text>
          </IndexTable.Cell>

          {/* Items (NEW) */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {ordersSummaryRow.totalItems || 0}
            </Text>
          </IndexTable.Cell>

          {/* Sales (renamed from Gross Revenue) */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.grossAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Refunds */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.refundAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Net Sales */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.netAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* MKT Commission */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.appliedMarketplaceCommissionAmount ?? 0)}
            </Text>
          </IndexTable.Cell>

          {/* 2W Commission */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.applied2wCommissionAmount ?? 0)}
            </Text>
          </IndexTable.Cell>

          {/* Shipped by - nothing to total, but the cell has to be here or every money
              column after it prints one place to the left. */}
          <IndexTable.Cell />

          {/* Shipping Fees */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.appliedShippingFee || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Return Fee */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(ordersSummaryRow.appliedReturnFee || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Net Payout */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold" tone={ordersSummaryRow.payout < 0 ? 'critical' : ordersSummaryRow.payout > 0 ? 'success' : 'base'}>
              {apiClient.formatCurrency(ordersSummaryRow.payout || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Actions - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>
        </IndexTable.Row>
      );

      return [...allRows, summaryRowMarkup];
    }

    return allRows;
  }, [orders, ordersSummaryRow, ordersSelectedResources, resyncingOrderId, handleResyncOrder, brand?.shopDomain]);

  // Available marketplaces state (using proper marketplace detection service)
  const [availableMarketplaceTags, setAvailableMarketplaceTags] = useState([]);
  const [availableMarketplacesLoading, setAvailableMarketplacesLoading] = useState(false);

  // Fetch available marketplaces using proper marketplace service
  useEffect(() => {
    if (!brandId) return;

    const fetchAvailableMarketplaces = async () => {
      try {
        setAvailableMarketplacesLoading(true);
        const response = await apiClient.request(`/brands/${brandId}/available-marketplaces`);

        // Don't filter out configured marketplaces here - let users configure any marketplace
        // The filtering should happen only in the dropdown options
        setAvailableMarketplaceTags(response.marketplaces || []);
      } catch (error) {
        console.error('Failed to fetch available marketplaces:', error);
        setAvailableMarketplaceTags([]);
      } finally {
        setAvailableMarketplacesLoading(false);
      }
    };

    fetchAvailableMarketplaces();
  }, [brandId]); // Remove marketplaceReturnFees dependency

  // Activity log state
  const [activityLog, setActivityLog] = useState([]);

  // Activity log paging. Its own state, independent of the Orders table's: the whole log is
  // held client-side, so the page is cut from the array in hand and no size change costs a
  // request.
  const [activityPage, setActivityPage] = useState(1);
  const [activityPageSize, setActivityPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncStartDate, setSyncStartDate] = useState('');
  const [syncEndDate, setSyncEndDate] = useState('');

  /**
   * The whole months the chosen dates actually cover.
   *
   * Dates choose months; months drive the sync. The worker keeps the start's own month when
   * the start date is the 1st and otherwise moves to the 1st of the next one, then syncs every
   * month up to the end date — and the end date never truncates the last one. So 1 to 20
   * August recalculates the WHOLE of August, while 5 to 20 August covers no month at all and
   * would report success having changed nothing.
   *
   * Returned rather than reduced to a yes/no, so the panel can state what will happen instead
   * of only warning when nothing will. Correcting the dates instead was considered and does
   * not work: with 5 to 31 August the START is what excludes August, and moving it back to the
   * 1st would silently widen what gets rewritten for someone who meant September.
   *
   * Plain year/month integers, never Date objects: `new Date('2026-09-01')` is UTC midnight
   * while getDate() and friends read local time, so west of UTC the end date slips back a day
   * and this would disagree with the server.
   */
  const recalculationMonths = useMemo(() => {
    const parts = (value) => {
      const [y, m, d] = String(value || '').split('-').map(Number);
      return [y, m, d].every(Number.isFinite) ? { y, m, d } : null;
    };
    const start = parts(syncStartDate);
    const end = parts(syncEndDate);
    if (!start || !end) return null;

    const rollsOver = start.d !== 1 && start.m === 12;
    let y = start.d === 1 ? start.y : (rollsOver ? start.y + 1 : start.y);
    let m = start.d === 1 ? start.m : (rollsOver ? 1 : start.m + 1);

    const months = [];
    // A month counts when its 1st falls on or before the end date, which for the same month is
    // true of any end day.
    while (y * 100 + m <= end.y * 100 + end.m && months.length < 36) {
      months.push(`${y}-${String(m).padStart(2, '0')}`);
      if (m === 12) { y += 1; m = 1; } else { m += 1; }
    }
    return months;
  }, [syncStartDate, syncEndDate]);

  /** "2026-08" reads as a date to a machine and as noise to a person. */
  const monthLabel = useCallback((value) => {
    const [y, m] = String(value || '').split('-').map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(m)) return value;
    return `${new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'long' })} ${y}`;
  }, []);

  const staleMonthLabels = useMemo(
    () => (syncStatus?.staleMonths ?? []).map(monthLabel),
    [syncStatus?.staleMonths, monthLabel],
  );
  const [syncForceSync, setSyncForceSync] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  /**
   * Brand Status flags, staged locally and written by Save.
   *
   * They used to write on every click. `approved` is the gate that decides whether the brand
   * can use the app at all, and the four module flags decide which sections they see — so a
   * mis-click revoked a brand's access with no confirmation, no undo, and no indication that
   * anything had been sent. Staging them costs one button and makes an accidental tick a
   * thing you can reverse before it leaves the screen.
   */
  const BRAND_FLAGS = [
    'approved',
    'billingEnabled',
    'reportingEnabled',
    'logisticsEnabled',
    'connectorEnabled',
  ];

  const flagsFromBrand = (b) => ({
    approved: b?.approved ?? false,
    billingEnabled: b?.billingEnabled ?? true,
    reportingEnabled: b?.reportingEnabled ?? false,
    logisticsEnabled: b?.logisticsEnabled ?? false,
    connectorEnabled: b?.connectorEnabled ?? false,
  });

  const [flagForm, setFlagForm] = useState(() => flagsFromBrand(brand));

  // Re-sync when the brand record itself changes, the same way CommissionSettings does. A
  // lazy initialiser runs once, so a component mounted before the brand loaded — or one
  // whose brand was re-fetched after a save elsewhere — would otherwise show the first
  // snapshot forever and Save would write stale values back.
  const flagFingerprint = `${brand?.id}|${BRAND_FLAGS.map((f) => String(brand?.[f])).join('|')}`;
  const [flagsSyncedFrom, setFlagsSyncedFrom] = useState(flagFingerprint);
  if (flagsSyncedFrom !== flagFingerprint) {
    setFlagsSyncedFrom(flagFingerprint);
    setFlagForm(flagsFromBrand(brand));
  }

  const flagsDirty = BRAND_FLAGS.some((f) => flagForm[f] !== flagsFromBrand(brand)[f]);

  /** Writes only the flags that actually changed, so Save cannot re-assert an untouched one. */
  const saveBrandFlags = async () => {
    const current = flagsFromBrand(brand);
    const changed = Object.fromEntries(
      BRAND_FLAGS.filter((f) => flagForm[f] !== current[f]).map((f) => [f, flagForm[f]]),
    );
    if (Object.keys(changed).length === 0) return;
    setSaving(true);
    try {
      await apiClient.updateBrand(brandId, changed);
      await fetchBrandData();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  // Refs for cleanup
  const syncPollingRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  const { data: brandAnalytics, isLoading: analyticsLoading, error: analyticsError } = useBrandAnalytics(
    brandId,
    getAnalyticsParams(),
    { enabled: !!brandId } // Always fetch analytics for overview cards
  );

  // Derived from BRAND_TABS so order is defined once. Filtering only ever drops the LAST
  // entry, which is what keeps Polaris' reported index aligned with tabIndex() — see the
  // invariant on BRAND_TABS.
  const tabs = BRAND_TABS
    .filter((t) => !t.requiresConnector || brand?.connectorEnabled)
    .map(({ requiresConnector, ...tab }) => tab);

  useEffect(() => {
    if (brandId) {
      fetchBrandData();
    }
  }, [brandId]);

  // Sync status is fetched on the ORDERS tab, which is where Sync & Recalculate now lives.
  //
  // This was keyed on tabIndex('sync'). Leaving it would have been worse than dead code:
  // tabIndex falls back to 0 for an unknown id, so with no tab carrying 'sync' it resolves to
  // the SETTINGS tab — the status fetch would fire on Settings and never on the tab that
  // actually shows the card. A ?tab=sync bookmark lands on Settings for the same reason, which
  // is a fair fallback.
  useEffect(() => {
    if (brandId && selectedTab === tabIndex('orders')) {
      fetchSyncStatus();
    }
  }, [brandId, selectedTab]);

  // Refetch orders when Orders tab is selected
  useEffect(() => {
    if (brandId && selectedTab === tabIndex('orders') && refetchOrders) {
      refetchOrders();
    }
  }, [brandId, selectedTab, refetchOrders]);

  // The retailer filter options for Queue and Sync History, fetched ONCE on first visit to
  // either tab. `runRetailersLoaded` rather than a length check: a brand with no connections
  // returns an empty list, and testing the length would re-request it on every tab switch
  // forever — which is the brand this fetch matters least for.
  useEffect(() => {
    const onRunsTab = selectedTab === tabIndex('queue') || selectedTab === tabIndex('history');
    if (!brandId || !onRunsTab || runRetailersLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const connections = await apiClient.getBrandMarketplaces(brandId);
        if (cancelled) return;
        setRunRetailerOptions(
          (connections || [])
            .filter((bm) => bm.enabled && bm.marketplace?.id)
            .map((bm) => ({ label: bm.marketplace.name, value: bm.marketplace.id })),
        );
      } catch {
        // A failed lookup costs the filter dropdown, not the tables — they scope themselves
        // server-side. Leaving the options empty is the honest degradation.
      } finally {
        if (!cancelled) setRunRetailersLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [brandId, selectedTab, runRetailersLoaded]);

  // Cleanup function needs to be defined before use
  const cleanupSyncPolling = useCallback(() => {
    if (syncPollingRef.current) {
      clearInterval(syncPollingRef.current);
      syncPollingRef.current = null;
    }
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  // Cleanup on component unmount and brand change
  useEffect(() => {
    return cleanupSyncPolling;
  }, [brandId]); // Clean up when brandId changes or component unmounts

  const fetchBrandData = useCallback(async () => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors

      if (!brandId || typeof brandId !== 'string') {
        throw new Error('Invalid brand ID provided');
      }

      const [brandData, activityData] = await Promise.all([
        apiClient.getBrand(brandId).catch(error => {
          // Only log detailed errors in development
          if (process.env.NODE_ENV === 'development') {
            console.error('Brand data fetch failed:', error);
          }
          throw new Error(`Failed to load brand details: ${error.message || 'Unknown error'}`);
        }),
        apiClient.getBrandActivity(brandId).catch(error => {
          // Only log warnings in development
          if (process.env.NODE_ENV === 'development') {
            console.warn('Activity data fetch failed, continuing without activity log:', error);
          }
          return []; // Return empty array if activity fetch fails
        })
      ]);

      // Validate brand data structure
      if (!brandData || typeof brandData !== 'object') {
        throw new Error('Invalid brand data received from server');
      }

      // Brand data fetched successfully - no need to log in production

      setBrand(brandData);
      const commissionRate = brandData.defaultCommissionRate || 0;
      setDefaultRate((commissionRate * 100).toString());
      setActivityLog(Array.isArray(activityData) ? activityData : []);

      // Initialize shipping enabled state from brand data
      setShippingEnabled(brandData.shippingEnabled !== false); // Default to true if undefined
      {
        const fee = brandData.fallbackShippingFee;
        const asText = fee === null || fee === undefined ? '' : String(fee);
        setFallbackFee(asText);
        setSavedFallbackFee(asText);
      }

      // Initialize return fees (both default and marketplace-specific) with global defaults support
      try {
        // Load return fees - API now returns global defaults and override indicators
        const returnFeeResponse = await apiClient.getDefaultReturnFee(brandId);

        // Set effective return fee (brand override or global default)
        setDefaultReturnFee((returnFeeResponse.defaultReturnFee || 0).toFixed(2));
        setHasDefaultReturnFee(returnFeeResponse.hasCustomFee || false);

        setReturnFeesUnavailable(false);

        // Set marketplace fees (merged: brand overrides + global)
        setMarketplaceReturnFees(returnFeeResponse.marketplaceFees || {});

        // Track which are brand-specific overrides (for blue highlighting)
        setReturnFeeOverrideKeys(returnFeeResponse.overrideKeys || []);

        // Store global defaults for reference
        if (returnFeeResponse.globalDefaults) {
          setGlobalReturnFeeDefaults(returnFeeResponse.globalDefaults);
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Return fees loaded:', {
            defaultFee: returnFeeResponse.defaultReturnFee,
            hasBrandOverride: returnFeeResponse.hasCustomFee,
            overrideKeys: returnFeeResponse.overrideKeys,
            globalDefaults: returnFeeResponse.globalDefaults
          });
        }
      } catch (returnFeeError) {
        // Return fees are optional, don't fail the whole page load.
        //
        // But do NOT invent 0.00. This used to set defaultReturnFee to '0.00' and
        // hasGlobalDefault to false, after which the page stated as fact "All marketplaces use
        // the default return fee of $0.00" and offered to override "$0.00" - an UNKNOWN fee
        // rendered as a ZERO fee, with the failure swallowed by a dev-only console.warn. Record
        // that the load failed and let the UI say so.
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to load return fees:', returnFeeError.message);
        }
        setDefaultReturnFee('');
        setHasDefaultReturnFee(false);
        setMarketplaceReturnFees({});
        setReturnFeeOverrideKeys([]);
        // Keep the object SHAPE (seven render sites read globalReturnFeeDefaults.<field>
        // directly, so null would throw); returnFeesUnavailable is what says "unknown".
        setGlobalReturnFeeDefaults({ default: 0, hasGlobalDefault: false, marketplaces: {} });
        setReturnFeesUnavailable(true);
      }
    } catch (err) {
      // Only log detailed errors in development - don't expose sensitive details in production
      if (process.env.NODE_ENV === 'development') {
        console.error('BrandConfiguration: Brand data fetch error:', err.message || 'Unknown error');
      }
      const errorMessage = err.message || 'Failed to load brand data';
      setError(errorMessage);

      // Reset state on error
      setBrand(null);
      setActivityLog([]);
    } finally {
      setLoading(false);
    }
  }, [brandId, apiClient]);

  const updateDefaultCommission = async () => {
    if (!brand) return;

    // Enhanced validation using input security
    const rateValidation = validateNumeric(defaultRate, {
      min: 0,
      max: 100,
      allowNegative: false
    });

    if (!rateValidation.isValid) {
      setError(`Commission rate error: ${rateValidation.error}`);
      return;
    }

    try {
      setSaving(true);
      await apiClient.updateBrand(brand.id, {
        defaultCommissionRate: rateValidation.sanitized / 100
      });
      await fetchBrandData();
      setError(''); // Clear any previous errors on success
    } catch (err) {
      setError(err.message || 'Failed to update commission rate');
    } finally {
      setSaving(false);
    }
  };

  const saveFallbackFee = async () => {
    if (!brand) return;
    const trimmed = fallbackFee.trim();
    const parsed = parseFloat(trimmed);
    if (trimmed !== '' && (!Number.isFinite(parsed) || parsed <= 0)) {
      setError('Enter a fallback shipping fee above zero, or leave it empty for none.');
      return;
    }
    try {
      setSaving(true);
      // Empty clears it. Null is a real value - it is what every brand has today, and it
      // means an unpriced order stays at $0 and stays flagged rather than being charged.
      await apiClient.updateBrand(brand.id, {
        fallbackShippingFee: trimmed === '' ? null : parsed,
      });
      setSavedFallbackFee(trimmed === '' ? '' : String(parsed));
      setFallbackFee(trimmed === '' ? '' : String(parsed));
      await fetchBrandData();
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to save the fallback shipping fee');
    } finally {
      setSaving(false);
    }
  };

  const updateShippingEnabled = async (enabled) => {
    if (!brand) return;

    try {
      setSaving(true);
      await apiClient.updateBrand(brand.id, {
        shippingEnabled: enabled
      });
      setShippingEnabled(enabled);
      await fetchBrandData(); // Refresh brand data
      setError(''); // Clear any previous errors on success
    } catch (err) {
      setError(err.message || 'Failed to update shipping configuration');
      // Revert the toggle on error
      setShippingEnabled(!enabled);
    } finally {
      setSaving(false);
    }
  };

  // Return fee management functions
  const updateDefaultReturnFee = async () => {
    if (!brand) return;

    const feeValidation = validateNumeric(defaultReturnFee, {
      min: 0,
      max: 1000,
      allowNegative: false
    });

    if (!feeValidation.isValid) {
      setError(`Return fee error: ${feeValidation.error}`);
      return;
    }

    try {
      setSaving(true);
      const response = await apiClient.updateDefaultReturnFee(brand.id, feeValidation.sanitized);

      // Check if backend returned isGlobal (value matched global, no override created/kept)
      if (response.isGlobal) {
        setHasDefaultReturnFee(false); // No brand-specific override
      } else {
        setHasDefaultReturnFee(true);
      }

      setError(''); // Clear any previous errors
      await fetchBrandData(); // Refresh data
    } catch (err) {
      setError(err.message || 'Failed to update default return fee');
    } finally {
      setSaving(false);
    }
  };

  const saveMarketplaceReturnFee = async (marketplace, fee) => {
    if (!brand) return;

    try {
      setSaving(true);
      const response = await apiClient.createMarketplaceReturnFee(brand.id, marketplace, fee);

      // Only update local state if backend actually created an override
      // If isGlobal is true, the value matched global and no override was created
      if (!response.isGlobal) {
        setMarketplaceReturnFees({
          ...marketplaceReturnFees,
          [marketplace]: parseFloat(fee)
        });
      }

      setShowReturnFeeModal(false);
      setEditingMarketplace('');
      setNewReturnFee('0.00'); // Reset for next use
      await fetchBrandData(); // Refresh brand data
    } catch (err) {
      setError(err.message || 'Failed to save marketplace return fee');
    } finally {
      setSaving(false);
    }
  };

  const deleteMarketplaceReturnFee = async (marketplace) => {
    if (!brand) return;
    if (!confirm(`Are you sure you want to delete the return fee for ${marketplace}?`)) return;

    try {
      setSaving(true);
      await apiClient.deleteMarketplaceReturnFee(brand.id, marketplace);
      const newFees = { ...marketplaceReturnFees };
      delete newFees[marketplace];
      setMarketplaceReturnFees(newFees);
      await fetchBrandData(); // Refresh brand data
    } catch (err) {
      setError(err.message || 'Failed to delete marketplace return fee');
    } finally {
      setSaving(false);
    }
  };

  // Reset default return fee to global default
  const handleResetDefaultFee = async () => {
    if (!brand) return;

    try {
      setResettingFees(true);
      // Use existing delete endpoint to remove brand-specific default return fee
      await apiClient.deleteDefaultReturnFee(brand.id);
      setShowResetDefaultFeeModal(false);
      await fetchBrandData(); // Refresh brand data
    } catch (err) {
      setError(err.message || 'Failed to reset default return fee');
    } finally {
      setResettingFees(false);
    }
  };

  // Reset all marketplace return fees to global defaults
  const handleResetMarketplaceFees = async () => {
    if (!brand) return;

    try {
      setResettingFees(true);
      const response = await apiClient.resetMarketplaceReturnFeesToDefault(brand.id);
      if (response.success) {
        setShowResetMarketplaceFeesModal(false);
        await fetchBrandData(); // Refresh brand data
      } else {
        throw new Error(response.message || 'Failed to reset marketplace return fees');
      }
    } catch (err) {
      setError(err.message || 'Failed to reset marketplace return fees');
    } finally {
      setResettingFees(false);
    }
  };

  const handleShippingPricingSave = async (changes, fullPricingData) => {
    if (!brand?.id) return;
    if (!changes || changes.length === 0) return;

    try {
      setSaving(true);
      setError('');

      // Transform changes into the format expected by the PATCH API.
      // weightMin/weightMax come from the change itself - ShippingPricingTable resolves them
      // against the bands it actually rendered (which include bands read from the database),
      // so there is no local copy of the weight ladder to drift out of sync.
      const updates = changes.map(change => ({
        shippingMethod: change.serviceId,
        weightMin: change.weightMin,
        weightMax: change.weightMax,
        fee: change.newValue ? parseFloat(change.newValue) : null, // null = delete/reset
        oldFee: change.oldValue ? parseFloat(change.oldValue) : null // for audit
      }));

      // Use PATCH endpoint for granular updates
      const response = await apiClient.request(`/brands/${brand.id}/shipping-fees`, {
        method: 'PATCH',
        body: JSON.stringify({ updates }),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to save shipping pricing');
      }
    } catch (err) {
      console.error('Error saving shipping pricing:', err);
      setError(`Failed to save shipping pricing: ${err.message}`);
      throw err; // Re-throw so the component can handle the error
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Page title="Brand Configuration" fullWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="200">
                <InlineStack align="center" gap="400">
                  <Spinner size="large" />
                  <BlockStack gap="200">
                    <Text variant="bodyLg" fontWeight="medium">Loading brand configuration...</Text>
                    <Text variant="bodySm" tone="subdued">Fetching commission rates and shipping settings</Text>
                  </BlockStack>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!brand) {
    return (
      <Page title="Brand Configuration" fullWidth>
        <Layout>
          <Layout.Section>
            <EmptyState
              heading="Brand not found"
              content="The requested brand could not be found or you don't have access to it."
              image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
              action={{
                content: 'Back to Brand Selection',
                onAction: () => navigate('/')
              }}
            />
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Sync functions

  // A hoisted `function`, deliberately, not a `const` arrow.
  //
  // It is declared here, below the `if (loading)` early return, but the effect that calls it
  // sits near the top of the component and fires on mount. On a first mount with ?tab=orders
  // the component returns early, so a `const` binding here is never initialised — and the
  // effect then threw `Cannot access 'fetchSyncStatus' before initialization` and took the
  // whole page down. A function declaration is hoisted and initialised on entry to the
  // component body, so it exists whichever branch the render took. Guarding the effect
  // instead would not do: its deps are [brandId, selectedTab], neither of which changes when
  // `loading` flips, so the status would simply never be fetched.
  async function fetchSyncStatus(retryCount = 0) {
    try {
      const status = await apiClient.getSyncStatus(brandId);
      setSyncStatus(status);

      // Clear errors on successful fetch
      if (error && error.includes('Failed to get sync status')) {
        setError('');
      }

      return status;
    } catch (error) {
      console.error('Failed to fetch sync status:', error);

      // Retry logic for temporary failures with timeout cleanup
      if (retryCount < 2 && (error.status >= 500 || error.status === 503)) {
        // Wait before retry with exponential backoff
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s

        // Use Promise-based delay that can be cleaned up
        return new Promise((resolve) => {
          const timeoutId = setTimeout(async () => {
            // Clear the timeout ref once it executes
            if (retryTimeoutRef.current === timeoutId) {
              retryTimeoutRef.current = null;
            }
            const result = await fetchSyncStatus(retryCount + 1);
            resolve(result);
          }, delay);

          // Store timeout ID for cleanup
          retryTimeoutRef.current = timeoutId;
        });
      }

      // Only show error if not retrying or final retry failed
      if (retryCount >= 2 || error.status < 500) {
        if (error.status === 404) {
          setError('Brand not found or sync status unavailable.');
        } else if (error.status === 503) {
          setError('Sync service temporarily unavailable. Status will update when service is restored.');
        } else {
          setError(`Failed to get sync status: ${error.message || 'Unknown error occurred'}`);
        }
      }

      return null;
    }
  }

  const handleInitiateSync = async () => {
    if (isSyncing) return;

    setIsSyncing(true);
    setError('');

    try {
      const syncOptions = {
        startDate: syncStartDate || undefined,
        endDate: syncEndDate || undefined,
        forceSync: syncForceSync
      };

      const result = await apiClient.syncOrders(brandId, syncOptions);

      // Update sync status immediately
      setSyncStatus({
        ...syncStatus,
        hasActiveJob: true,
        currentJob: {
          jobId: result.jobId,
          state: 'waiting',
          progress: 0
        }
      });

      // Poll for status updates
      pollSyncStatus();

    } catch (error) {
      console.error('Failed to initiate sync:', error);

      // Cleanup any running polls on error
      cleanupSyncPolling();
      setIsSyncing(false);

      // Enhanced error handling with specific messages
      if (error.status === 423) {
        // 423 Locked - distributed lock held by another admin
        const errorData = error.data || {};
        setError(`Sync locked by ${errorData.currentLockHolder || 'another admin'} since ${errorData.lockAcquiredAt ? new Date(errorData.lockAcquiredAt).toLocaleString() : 'unknown time'}. Use "Force Sync" to override.`);
      } else if (error.status === 409) {
        setError('Sync already in progress for this brand. Use "Force Sync" to override.');
      } else if (error.status === 400) {
        // Handle validation errors
        const errorMessage = error.message || 'Invalid sync parameters';
        if (errorMessage.includes('session')) {
          setError('Shopify connection issue: The app may need to be reinstalled for this brand.');
        } else if (errorMessage.includes('date')) {
          setError(`Date validation error: ${errorMessage}`);
        } else if (errorMessage.includes('inactive')) {
          setError('Cannot sync inactive brand. Please activate the brand first.');
        } else {
          setError(`Validation error: ${errorMessage}`);
        }
      } else if (error.status === 503) {
        setError('Sync service temporarily unavailable. Please try again in a moment.');
      } else if (error.status >= 500) {
        setError('Server error occurred. Please try again or contact support if the issue persists.');
      } else if (!navigator.onLine) {
        setError('No internet connection. Please check your network and try again.');
      } else {
        setError(`Failed to start sync: ${error.message || 'Unknown error occurred'}`);
      }
    }
  };

  const pollSyncStatus = () => {
    // Clean up any existing polling first
    cleanupSyncPolling();

    let pollAttempts = 0;
    const maxPollAttempts = 360; // 30 minutes at 5-second intervals

    syncPollingRef.current = setInterval(async () => {
      try {
        pollAttempts++;

        // Stop polling after max attempts to prevent indefinite polling
        if (pollAttempts >= maxPollAttempts) {
          cleanupSyncPolling();
          setIsSyncing(false);
          setError('Sync monitoring stopped after 30 minutes. Please check sync status manually.');
          return;
        }

        const status = await fetchSyncStatus();

        if (!status?.hasActiveJob) {
          cleanupSyncPolling();
          setIsSyncing(false);

          // Clear the current job from state when sync completes.
          //
          // prevStatus first, and `status` only when the poll actually returned something:
          // fetchSyncStatus resolves to null on a handled failure, and spreading that left
          // syncStatus as { currentJob: null } — truthy, but with hasActiveJob and staleMonths
          // gone. The panel then read as a clean "Ready" with no months outstanding, which is
          // an assertion rather than an absence of information.
          setSyncStatus(prevStatus => ({
            ...prevStatus,
            ...(status ?? {}),
            currentJob: null
          }));

          // Show completion message if sync was successful
          if (status?.brandSyncStatus === 'completed') {
            // Clear any previous errors on successful completion
            setError('');
          }
        }
      } catch (error) {
        console.error('Error polling sync status:', error);

        // Don't immediately stop polling on single error - could be temporary network issue
        if (pollAttempts >= 3) {
          // Only show error after multiple attempts
          setError(`Failed to get sync status: ${error.message}`);
        }

        // Stop polling after 5 consecutive errors
        if (pollAttempts >= 5) {
          cleanupSyncPolling();
          setIsSyncing(false);
        }
      }
    }, 5000);
  };

  const canInitiateSync = () => {
    return brand?.isActive && !isSyncing && (!syncStatus?.hasActiveJob || syncForceSync);
  };


  /**
   * Sync & Recalculate — rendered at the TOP of the Orders tab, not on a tab of its own.
   *
   * "Force recalculation with current settings" rewrites Marketplace Commission, 2W Commission,
   * Shipping Fees and Net Payout on existing orders — the exact columns the table below it
   * shows. On its own "Data Sync" tab you ticked a box in one place and had to navigate
   * elsewhere to see whether the figures had moved, with nothing on either screen saying the
   * two were connected. Above the table the sequence reads: choose the range, recalculate,
   * watch those columns change.
   *
   * Returns a Layout.Section rather than its own Layout so it composes into renderOrders.
   */
  const renderOrderSyncSection = () => (
      <Layout.Section>
        <Card>
          <Box padding="200">
            <BlockStack gap="300">
              {/* Well-spaced Header with Status */}
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="050">
                  <Text variant="headingMd" as="h3" fontWeight="semibold">
                    Sync &amp; Recalculate
                  </Text>
                  <Text variant="bodyMd" tone="subdued">
                    Sync historical orders and recalculate billing with current settings
                  </Text>
                </BlockStack>
                <InlineStack gap="300" blockAlign="center">
                  {syncStatus?.lastSyncDate && !syncStatus.hasActiveJob && (
                    <Text variant="bodySm" tone="subdued">
                      Last sync: {new Date(syncStatus.lastSyncDate).toLocaleDateString()}
                    </Text>
                  )}
                  {syncStatus && (
                    <Badge tone={syncStatus.hasActiveJob ? 'info' : (syncStatus.staleMonths?.length ? 'attention' : 'success')}>
                      {syncStatus.hasActiveJob
                        ? 'Syncing'
                        : (syncStatus.staleMonths?.length ? 'Needs recalculation' : 'Ready')}
                    </Badge>
                  )}
                </InlineStack>
              </InlineStack>

              {/* Months whose stored figures were worked out under settings that have since
                  changed. A settings change rebuilds the current month and flags the rest,
                  and storing a report clears its own flag, so what is listed here is what an
                  admin still has to decide about. Hidden while a sync is running, when the
                  list is about to change anyway. */}
              {!syncStatus?.hasActiveJob && syncStatus?.staleMonths?.length > 0 && (
                <Banner tone="warning">
                  <BlockStack gap="100">
                    {/* Not "was billed": this list can include the current month, which nobody
                        has been billed for yet. A commission-override or shipping-fee save
                        flags every month and regenerates none, so the current month can sit
                        here legitimately. What is true of all of them is that their stored
                        figures came from settings that have since changed. */}
                    <Text variant="bodySm" fontWeight="semibold">
                      {syncStatus.staleMonths.length === 1
                        ? '1 month no longer matches current settings'
                        : `${syncStatus.staleMonths.length} months no longer match current settings`}
                    </Text>
                    <Text variant="bodySm">{staleMonthLabels.join(' \u00B7 ')}</Text>
                    <Text variant="bodySm" tone="subdued">
                      Their stored figures were worked out under earlier settings and do not
                      change on their own. To apply current settings to one, set the dates below
                      to cover that whole month and tick Force recalculation.
                    </Text>
                  </BlockStack>
                </Banner>
              )}

              {/* Balanced Progress Indicator */}
              {(syncStatus?.hasActiveJob || isSyncing) && syncStatus?.currentJob && (
                <Box padding="400" background="bg-surface-info-subdued" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <InlineStack gap="300" blockAlign="center">
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid #e1e3e5',
                        borderTop: '2px solid #006fff',
                        borderRadius: '50%',
                        animation: 'syncSpin 1s linear infinite'
                      }} />
                      <Text variant="bodyMd" fontWeight="medium">Syncing orders...</Text>
                    </InlineStack>
                    <Badge tone="info">
                      {Math.round(syncStatus.currentJob.progress || 0)}%
                    </Badge>
                  </InlineStack>
                  <style>{`
                    @keyframes syncSpin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </Box>
              )}

              {/* No "Configuration" label.
                  
                  It named the obvious: three labelled inputs and a checkbox under a card
                  headed "Order Data Sync" are self-evidently the configuration, and the label
                  cost a line of vertical space plus a 300 gap beneath it. */}
              <BlockStack gap="300">
                  {/* No floor on either picker. Shopify caps order reads at 60 days only
                      without read_all_orders, which this app requests on every install, and
                      the worker does not clamp either — its shopifyApiLimitDays only fills in
                      reporting fields, since the sync is driven by whole month strings. The
                      floor mattered more once a forced resync became the ONLY way to change a
                      past month: a month flagged as no longer matching current settings would
                      have become uncorrectable as soon as it aged out of the window. */}
                  {/* grid-3 in the wireframe: equal thirds across the card, not fixed
                      150/150/120px boxes. */}
                  {/* Two fields, not three. "Max Months" is gone: it was sent to the API,
                      forwarded into the job's options, and never read — the worker's own comment
                      said "no maxMonths cap". So it promised a ceiling it did not apply, and a
                      start date of Jan 2024 with Max Months 2 synced twenty months. The dates
                      are what decide the span, and always were. */}
                  <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
                    <TextField
                      label={<Text variant="bodyMd" fontWeight="semibold" as="span">Start Date</Text>}
                      type="date"
                      value={syncStartDate}
                      onChange={setSyncStartDate}
                      disabled={isSyncing}
                    />
                    <TextField
                      label={<Text variant="bodyMd" fontWeight="semibold" as="span">End Date</Text>}
                      type="date"
                      value={syncEndDate}
                      onChange={setSyncEndDate}
                      max={formatDateLocal(new Date())}
                      disabled={isSyncing}
                    />
                  </InlineGrid>

                {/* Recalculation works on whole months. The worker keeps the start month when
                    the start date is the 1st and otherwise moves to the 1st of the next one,
                    and the month it lands on drives the sync — the end date never truncates
                    it. So 1 to 20 August recalculates the whole of August, while 5 to 20
                    August recalculates nothing and still reports success. Caught here rather
                    than after the fact. */}
                {recalculationMonths !== null && (
                  recalculationMonths.length === 0 ? (
                    <Banner tone="warning">
                      <Text variant="bodySm">
                        This range contains no complete month, so nothing would be recalculated.
                        Recalculation works on whole months &mdash; start on the 1st to cover
                        that month.
                      </Text>
                    </Banner>
                  ) : (
                    <Text variant="bodySm" tone="subdued">
                      Will recalculate: {recalculationMonths.map(monthLabel).join(', ')}
                    </Text>
                  )
                )}

                {/* No tint behind this checkbox. It was the only option on the card, so the
                    band was not separating it from anything — it just added a second surface
                    colour inside a white card. */}
                <Checkbox
                  label={<Text variant="bodyMd" fontWeight="semibold" as="span">Force recalculation with current settings</Text>}
                  checked={syncForceSync}
                  onChange={setSyncForceSync}
                  disabled={isSyncing}
                  helpText="Recalculate all fees using your latest commission and shipping configuration"
                />

                {/* Action Buttons Section */}
                <InlineStack gap="300" align="start">
                  <Button
                    variant="primary"
                    size="large"
                    onClick={handleInitiateSync}
                    loading={isSyncing}
                    disabled={!canInitiateSync()}
                  >
                    {syncStatus?.hasActiveJob && !syncForceSync ? 'Syncing...' : 'Start Sync'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="large"
                    onClick={fetchSyncStatus}
                    disabled={isSyncing}
                  >
                    Refresh Status
                  </Button>
                </InlineStack>
              </BlockStack>

              {/* Error Display */}
              {(error || (syncStatus?.lastSyncError && !syncStatus.hasActiveJob)) && (
                <Banner tone="critical">
                  <Text>{error || syncStatus.lastSyncError}</Text>
                </Banner>
              )}
            </BlockStack>
          </Box>
        </Card>
      </Layout.Section>
  );

  const renderShippingConfiguration = () => {
    return (
      <BlockStack gap="600">
        {/* Shipping Configuration, with the pricing table as its second section.

            The table is what the toggle switches on, and the toggle's own description
            ("Configure BoxC shipping pricing for different weight ranges") is a sentence about
            the table. Two cards read as two settings; a divider keeps them one. */}
        <Card>
          <Box padding="200">
            <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="050">
                <Text variant="headingMd" as="h3">Shipping Configuration</Text>
                {/* What the closed section holds, so it can stay closed: the 2W locations and
                    the fallback fee, or why there is nothing below. */}
                <Text variant="bodyMd" tone="subdued">
                  {shippingSummary({ enabled: shippingEnabled, locationCount: shippingLocationCount, fallbackFee: savedFallbackFee })}
                </Text>
              </BlockStack>
              <InlineStack gap="200" blockAlign="center">
                <Text variant="bodyMd" fontWeight="medium">
                  {shippingEnabled ? 'Enabled' : 'Disabled'}
                </Text>
                <Button
                  variant={shippingEnabled ? 'secondary' : 'primary'}
                  tone={shippingEnabled ? 'critical' : 'success'}
                  onClick={() => updateShippingEnabled(!shippingEnabled)}
                  loading={saving}
                  disabled={saving || isSyncing}
                >
                  {shippingEnabled ? 'Disable Shipping' : 'Enable Shipping'}
                </Button>
                {shippingEnabled && (
                  <SectionToggle open={shippingOpen} onToggle={toggleShipping} controls="shipping-configuration-body" />
                )}
              </InlineStack>
            </InlineStack>

            {shippingEnabled && (
              <SectionBody open={shippingOpen} id="shipping-configuration-body">
              <BlockStack gap="300">
                {/* Above the pricing table, because it is the narrower question and it
                    decides whether the table applies at all: this says which shipments are
                    2W's to charge for, and the table then says what those cost. Inside the
                    shippingEnabled block for the same reason — with shipping off nothing is
                    charged and there is nothing to scope. */}
                <Divider />
                <TwShippingLocations
                  brand={brand}
                  saving={saving}
                  setSaving={setSaving}
                  setError={setError}
                  fetchBrandData={fetchBrandData}
                  disabled={isSyncing}
                />

                {/* AFTER the locations and BEFORE the rate card, because that is the order
                    the questions come in: which shipments are 2W's, what to charge when we
                    cannot price one, and then what the priced ones cost. */}
                <Divider />
                <BlockStack gap="300">
                  <BlockStack gap="050">
                    <Text variant="headingMd" as="h3">Fallback shipping fee</Text>
                    <Text variant="bodyMd" tone="subdued">
                      What to charge for an order 2W shipped when BoxC recorded no service to
                      price it from. Leave it empty to charge nothing, which is what happens
                      today.
                    </Text>
                  </BlockStack>
                  <InlineStack gap="200" blockAlign="end">
                    <Box minWidth="180px">
                      <TextField
                        label="Fallback fee"
                        labelHidden
                        type="number"
                        prefix="$"
                        value={fallbackFee}
                        onChange={setFallbackFee}
                        placeholder="None"
                        autoComplete="off"
                        disabled={saving || isSyncing}
                        min="0"
                        step="0.01"
                      />
                    </Box>
                    <Button
                      onClick={saveFallbackFee}
                      loading={saving}
                      disabled={saving || isSyncing || fallbackFee.trim() === savedFallbackFee.trim()}
                    >
                      Save
                    </Button>
                  </InlineStack>
                  {/* The two things an admin would otherwise have to be told. It is not
                      charged when the order syncs, so there is still time to put the BoxC
                      details right; and it never lands on an order we cannot attribute. */}
                  <Text variant="bodySm" tone="subdued">
                    Applied only where the order shipped from a location ticked as 2W&rsquo;s, and
                    only when the month is reported &mdash; so an order flagged &ldquo;No BoxC
                    service recorded&rdquo; can still be corrected first and priced from the rate
                    card instead. Orders we cannot attribute are never charged this.
                  </Text>
                </BlockStack>

                <Divider />
                <BlockStack gap="300">
                  <BlockStack gap="050">
                    <Text variant="headingMd" as="h3">BoxC Shipping Pricing</Text>
                    <Text variant="bodyMd" tone="subdued">
                      Override pricing for BoxC services across different weight ranges. Click any price cell to edit.
                    </Text>
                  </BlockStack>
                  <ShippingPricingTable
                    brand={brand}
                    onSave={handleShippingPricingSave}
                    loading={saving}
                    disabled={isSyncing}
                  />
                </BlockStack>
              </BlockStack>
              </SectionBody>
            )}
            </BlockStack>
          </Box>
        </Card>

        {/* Storage Fees - charged per unit held at the 2W locations ticked above. */}
        <Card>
          <Box padding="200">
            <StorageFeeSettings
              brand={brand}
              shippingEnabled={shippingEnabled}
              saving={saving}
              setSaving={setSaving}
              setError={setError}
            />
          </Box>
        </Card>

        {/* Default Return Fee */}
        <Card>
          <Box padding="200">
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center" gap="200">
                <BlockStack gap="050">
                  <Text variant="headingMd" as="h3">Default Return Fee</Text>
                  <Text variant="bodyMd" tone="subdued">
                    {returnFeeSummary({
                      hasDefault: hasDefaultReturnFee,
                      defaultFee: defaultReturnFee,
                      isOverride: returnFeeOverrideKeys.includes('default'),
                      globalDefault: globalReturnFeeDefaults.hasGlobalDefault ? globalReturnFeeDefaults.default : null,
                      overrideCount: returnFeeOverrideKeys.filter((key) => key !== 'default').length,
                      unavailable: returnFeesUnavailable,
                    })}
                  </Text>
                </BlockStack>
                <SectionToggle open={returnFeeOpen} onToggle={toggleReturnFee} controls="default-return-fee-body" />
              </InlineStack>

              <SectionBody open={returnFeeOpen} id="default-return-fee-body">
              <BlockStack gap="300">
              <Text variant="bodyMd" tone="subdued">
                Set a default return fee applied to all orders. Can be overridden per marketplace below.
              </Text>

              {/* Show global default info if brand doesn't have override */}
              {!hasDefaultReturnFee && globalReturnFeeDefaults.hasGlobalDefault && (
                <Banner tone="info">
                  <Text variant="bodySm">
                    Using global default: <strong>${globalReturnFeeDefaults.default.toFixed(2)}</strong>.
                    Set a brand-specific fee below to override this.
                  </Text>
                </Banner>
              )}

              {/* No bordered wrapper. Once the label and helpText went, this was a
                  full-width bordered band holding one short row, so most of it was empty. The
                  override highlight it used to carry is not lost: the info line below still
                  states when the brand has a custom override. */}
              <InlineStack gap="300" blockAlign="center">
                  <Box minWidth="200px">
                    {/* Label hidden and helpText dropped: the card heading directly above
                        already says "Default Return Fee", and its description already says the
                        fee applies to all orders and can be overridden per marketplace. The
                        override case is not lost either — the info line below this Box states
                        it, so the helpText was a third copy. */}
                    <TextField
                      label="Default return fee"
                      labelHidden
                      type="number"
                      value={defaultReturnFee}
                      onChange={setDefaultReturnFee}
                      prefix="$"
                      min="0"
                      max="1000"
                      step="0.01"
                    />
                  </Box>
                  <Button
                    variant="primary"
                    onClick={updateDefaultReturnFee}
                    loading={saving}
                    disabled={saving || isSyncing}
                    icon={CheckCircleIcon}
                  >
                    {hasDefaultReturnFee ? 'Update' : 'Set'}
                  </Button>
                  {returnFeeOverrideKeys.includes('default') && (
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => setShowResetDefaultFeeModal(true)}
                      disabled={saving || isSyncing || resettingFees}
                    >
                      Reset to Default
                    </Button>
                  )}
                </InlineStack>
              {returnFeeOverrideKeys.includes('default') && (
                <Text variant="bodySm" tone="info">
                  This brand has a custom override. Global default is ${globalReturnFeeDefaults.default.toFixed(2)}.
                </Text>
              )}

              {hasDefaultReturnFee && !returnFeeOverrideKeys.includes('default') && (
                <Banner tone="info">
                  <Text variant="bodySm">
                    Current default return fee: <strong>{returnFeesUnavailable || defaultReturnFee === '' ? 'not loaded' : `$${parseFloat(defaultReturnFee).toFixed(2)}`}</strong>.
                    This applies to all orders unless a marketplace-specific override exists below.
                  </Text>
                </Banner>
              )}

              {saving && <ProgressBar progress={undefined} size="small" />}

              {/* One card, two sections. The overrides ARE the exceptions to the fee above —
                  the description says "Can be overridden per marketplace below" and the empty
                  state says "No default return fee is set", both sentences about the other
                  half. Two cards framed them as unrelated settings. Same treatment as
                  Commission Rates and its Marketplace Overrides. */}
              <Divider />

              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="050">
                  <Text variant="headingMd" as="h3">Marketplace Overrides</Text>
                  <Text variant="bodyMd" tone="subdued">
                    Override the default return fee for specific marketplaces.
                    <span style={{ color: '#0ea5e9' }}> Blue rows</span> indicate brand-specific overrides.
                  </Text>
                </BlockStack>
                {/* Reset before Add: the destructive action is the secondary one, so it goes
                    left of the primary, and it stops "Reset All to Default" sitting to the
                    right of the button an admin presses routinely. */}
                <InlineStack gap="300" blockAlign="center">
                  {returnFeeOverrideKeys.filter(key => key !== 'default').length > 0 && (
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => setShowResetMarketplaceFeesModal(true)}
                      disabled={saving || isSyncing || resettingFees}
                    >
                      Reset All to Default ({returnFeeOverrideKeys.filter(key => key !== 'default').length})
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    icon={PlusIcon}
                    onClick={() => setShowReturnFeeModal(true)}
                    disabled={availableMarketplacesLoading || saving || isSyncing}
                  >
                    Add
                  </Button>
                </InlineStack>
              </InlineStack>

              {/* Marketplace Return Fees.

                  RENDERED EVEN WITH NOTHING IN IT. The table used to be dropped for a bare
                  tinted paragraph, so a brand with no overrides never saw that an override is
                  a marketplace, a fee, where the fee came from and a way to remove it.
                  DataTable has no `emptyState`, but it derives a cell's colSpan from the row's
                  length against headings.length (DataTable.js getColSpan), so a one-cell row
                  spans the table: the headings survive and the message becomes that row. Its
                  three cases are the original wording, unchanged — a failed load, a default in
                  force, and no default at all are three different statements. */}
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text']}
                headings={['Marketplace', 'Return Fee', 'Source', 'Actions'].map((h) => (
                  <Text key={h} variant="bodyMd" fontWeight="semibold" as="span">{h}</Text>
                ))}
                rows={Object.keys(marketplaceReturnFees).length === 0 ? [[
                  <Box key="no-marketplace-fees" padding="400" background="bg-fill-info-secondary" borderRadius="200">
                    <Text variant="bodySm" tone="subdued">
                      {hasDefaultReturnFee || globalReturnFeeDefaults.hasGlobalDefault
                        ? (returnFeesUnavailable || defaultReturnFee === ''
                            ? 'Return fees could not be loaded for this brand, so the default in force is unknown. Reload to try again.'
                            : `No marketplace overrides configured. All marketplaces use the default return fee of $${parseFloat(defaultReturnFee).toFixed(2)}.`)
                        : 'No marketplace overrides configured. No default return fee is set.'}
                    </Text>
                  </Box>,
                ]] : Object.entries(marketplaceReturnFees).map(([marketplace, fee]) => {
                  const isBrandOverride = returnFeeOverrideKeys.includes(marketplace);
                  const globalFee = globalReturnFeeDefaults.marketplaces?.[marketplace];
                  // Every highlighted cell gets the SAME minHeight and centres its content.
                  //
                  // Each cell wrapped its own content in a tinted Box, and the Box hugged
                  // whatever was inside — a Badge is taller than a bare currency string,
                  // which is taller than bodySm Text — so the three blue blocks on one row
                  // were three different heights and the row looked misaligned rather than
                  // highlighted. A shared minHeight plus centring makes the tint read as one
                  // continuous band, which is what "Blue rows" in the description promises.
                  const cellTint = {
                    padding: '100',
                    borderRadius: '100',
                    minHeight: '1.75rem',
                    background: isBrandOverride ? 'bg-surface-info' : 'bg-surface',
                  };
                  return [
                    <Box key={`mp-${marketplace}`} {...cellTint}>
                      <InlineStack blockAlign="center" gap="100">
                        <Badge tone={isBrandOverride ? 'info' : 'default'}>{marketplace}</Badge>
                      </InlineStack>
                    </Box>,
                    <Box key={`fee-${marketplace}`} {...cellTint}>
                      <InlineStack blockAlign="center" gap="100">
                        <Text variant="bodyMd" as="span">{formatCurrency(parseFloat(fee) || 0)}</Text>
                      </InlineStack>
                    </Box>,
                    <Box key={`src-${marketplace}`} {...cellTint}>
                      <InlineStack blockAlign="center" gap="100">
                        <Text variant="bodySm" tone={isBrandOverride ? 'info' : 'subdued'}>
                          {isBrandOverride ? 'Brand Override' : 'Global Default'}
                          {isBrandOverride && globalFee !== undefined && ` (global: $${globalFee.toFixed(2)})`}
                        </Text>
                      </InlineStack>
                    </Box>,
                    isBrandOverride ? (
                      <Button
                        key={`del-${marketplace}`}
                        variant="tertiary"
                        tone="critical"
                        size="micro"
                        onClick={() => deleteMarketplaceReturnFee(marketplace)}
                        loading={saving}
                      >
                        Remove
                      </Button>
                    ) : (
                      <Text key={`na-${marketplace}`} variant="bodySm" tone="subdued">—</Text>
                    )
                  ];
                })}
              />
              </BlockStack>
              </SectionBody>
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>
    );
  };

  const renderAnalytics = () => (
    <BlockStack gap="300">
      {/* Period selector, first and on its own.

          It used to be the LAST thing on the tab, inside a Card headed "Performance Period" —
          below every figure it governs. A filter that appears after its own results reads as
          something that happened to them rather than something you set, and you had to scroll
          past all eleven metric cards to find out which period they covered.

          No card and no label either. The card was a full-width surface holding one dropdown,
          and "Performance Period" only named what the button already says: the button reads
          "Last Month". The Popover keeps an accessible name through its own button text. */}
      <InlineStack align="end">
        <Popover
          active={periodPopoverActive}
          onClose={() => setPeriodPopoverActive(false)}
          preferredAlignment="right"
          activator={(
            <Button
              onClick={() => setPeriodPopoverActive((v) => !v)}
              disclosure
            >
              {periodLabel}
            </Button>
          )}
        >
          <ActionList
            actionRole="menuitem"
            items={[
              ...PERIOD_PRESETS.map((o) => ({
                content: o.label,
                // Marks the current row, which a Select gave for free and a Popover does
                // not. Without it nothing on screen says which period is in effect once
                // the menu is open.
                active: dateRangeMode === 'preset'
                  ? analyticsFilter === o.value
                  : dateRangeMode === o.value,
                onAction: () => {
                  setPeriodPopoverActive(false);
                  if (o.value === 'lastMonth' || o.value === 'thisMonth') {
                    setDateRangeMode(o.value);
                  } else {
                    setAnalyticsFilter(o.value);
                    setDateRangeMode('preset');
                  }
                },
              })),
              {
                content: dateRangeMode === 'custom' && customStartDate && customEndDate
                  ? `${customStartDate} to ${customEndDate}`
                  : 'Custom range...',
                active: dateRangeMode === 'custom',
                onAction: () => {
                  setPeriodPopoverActive(false);
                  openCustomDatePicker();
                },
              },
            ]}
          />
        </Popover>
      </InlineStack>

      {/* Brand Analytics Cards — six per row at xl, four at lg.
      
          Polaris Grid is 12 columns, so a span of 2 gives six per row and 3 gives four. With
          eleven cards that is 6 + 5: two rows and a single empty slot, against the 4 + 4 + 3 of
          a span of 3. Five per row was considered and rejected — 12/5 is not expressible here,
          and it would leave the third row holding one card beside four empty slots.
      
          The cost is width: at six across, "Marketplace Commission" wraps to two lines. The
          Grid.Cell flex stretch keeps the row heights level, so a wrapped label makes the whole
          row taller rather than misaligning it. */}
      <Grid gap="400">
        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 2 }} style={{ display: 'flex' }}>
          <OrdersCard
            title="Orders"
            value={brandAnalytics?.totalOrders || 0}
            loading={analyticsLoading}
            error={analyticsError}
            // subtitle={`AOV: ${apiClient.formatCurrency(brandAnalytics?.averageOrderValue || 0)}`}
            icon={OrderIcon}
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 2 }} style={{ display: 'flex' }}>
          <RevenueCard
            title="Gross Revenue"
            value={brandAnalytics?.totalGrossRevenue || 0}
            loading={analyticsLoading}
            error={analyticsError}
            format="currency"
            // change={brandAnalytics && {
            //   percentage: brandAnalytics.monthlyGrowth || 0,
            //   direction: brandAnalytics.monthlyGrowth >= 0 ? 'up' : 'down',
            //   period: `from last ${analyticsFilter} days`,
            // }}
            // subtitle={`${brand.name} performance`}
            icon={CashDollarIcon}
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 2 }} style={{ display: 'flex' }}>
          <RevenueCard
            title="Discounts"
            value={brandAnalytics?.totalDiscounts || 0}
            loading={analyticsLoading}
            error={analyticsError}
            format="currency"
            // change={brandAnalytics && {
            //   percentage: brandAnalytics.monthlyGrowth || 0,
            //   direction: brandAnalytics.monthlyGrowth >= 0 ? 'up' : 'down',
            //   period: `from last ${analyticsFilter} days`,
            // }}
            // subtitle={`${brand.name} performance`}
            icon={CashDollarIcon}
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 2 }} style={{ display: 'flex' }}>
          <MetricCard
            title="Refunds"
            value={brandAnalytics?.totalRefunds || 0}
            loading={analyticsLoading}
            error={analyticsError}
            formatter={(value) => (() => {
              try {
                return apiClient.formatCurrency(value);
              } catch (error) {
                return '$0.00';
              }
            })()}
            // change={{
            //   percentage: 0,
            //   direction: 'neutral',
            //   period: `vs. previous ${analyticsFilter} days`,
            // }}
            // subtitle="Total amount refunded"
            icon={ReceiptRefundIcon}
            color="critical"
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 2 }} style={{ display: 'flex' }}>
          <RevenueCard
            title="Net Sales"
            value={brandAnalytics?.totalRevenue || 0}
            loading={analyticsLoading}
            error={analyticsError}
            format="currency"
            // change={brandAnalytics && {
            //   percentage: brandAnalytics.monthlyGrowth || 0,
            //   direction: brandAnalytics.monthlyGrowth >= 0 ? 'up' : 'down',
            //   period: `from last ${analyticsFilter} days`,
            // }}
            // subtitle={`${brand.name} performance`}
            icon={CashDollarIcon}
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 2 }} style={{ display: 'flex' }}>
          <CommissionCard
            title="Marketplace Commission"
            value={brandAnalytics?.commissionBreakdown?.baseMarketplaceCommission || 0}
            loading={analyticsLoading}
            error={analyticsError}
            formatter={(value) => apiClient.formatCurrency(value)}
            icon={CashDollarIcon}
            tooltip="Marketplace commission from fulfilled orders (before adjustments)"
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 2 }} style={{ display: 'flex' }}>
          <CommissionCard
            title="2W Commission"
            value={brandAnalytics?.commissionBreakdown?.base2wCommission || 0}
            loading={analyticsLoading}
            error={analyticsError}
            formatter={(value) => apiClient.formatCurrency(value)}
            icon={CashDollarIcon}
            tooltip="2W service commission from fulfilled orders (before adjustments)"
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 2 }} style={{ display: 'flex' }}>
          <MetricCard
            title="Commission Adj."
            value={brandAnalytics?.commissionBreakdown?.totalAdjustmentAmount || 0}
            loading={analyticsLoading}
            error={analyticsError}
            formatter={(value) => {
              try {
                const prefix = value >= 0 ? '+' : '';
                return `${prefix}${apiClient.formatCurrency(value)}`;
              } catch (error) {
                return `$${(value || 0).toFixed(2)}`;
              }
            }}
            icon={CashDollarIcon}
            tooltip="Manual commission adjustments"
          />
        </Grid.Cell>


        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 2 }}>
          <MetricCard
            title="Shipping Fees"
            value={brandAnalytics?.totalShippingFees || 0}
            loading={analyticsLoading}
            error={analyticsError}
            formatter={(value) => (() => {
              try {
                return apiClient.formatCurrency(value);
              } catch (error) {
                return '$0.00';
              }
            })()}
            // change={{
            //   percentage: 0,
            //   direction: 'neutral',
            //   period: `vs. previous ${analyticsFilter} days`,
            // }}
            icon={StoreIcon}
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 2 }}>
          <MetricCard
            title="Return Fees"
            value={brandAnalytics?.totalReturnFees || 0}
            loading={analyticsLoading}
            error={analyticsError}
            formatter={(value) => (() => {
              try {
                return apiClient.formatCurrency(value);
              } catch (error) {
                return '$0.00';
              }
            })()}
            // change={{
            //   percentage: 0,
            //   direction: 'neutral',
            //   period: `vs. previous ${analyticsFilter} days`,
            // }}
            icon={ReturnIcon}
            color="warning"
          />
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 2 }} style={{ display: 'flex' }}>
          <RevenueCard
            title="Net Payout"
            value={brandAnalytics?.totalPayout || 0}
            loading={analyticsLoading}
            error={analyticsError}
            formatter={(value) => apiClient.formatCurrency(value)}
            icon={CashDollarIcon}
          />
        </Grid.Cell>

      </Grid>

      {/* Fulfillment & shipping — the second half of this tab's metrics, not a separate
          section. It renders its own Grid with the same columnSpan as the tiles above, so the
          two read as one grid split by a heading.

          Passed getAnalyticsParams() rather than analyticsFilter, which is stale whenever
          dateRangeMode is lastMonth, thisMonth or custom — the same reason MarketplaceBreakdown
          below reads its dates from the same call. */}
      <LogisticsBreakdown brandId={brandId} params={getAnalyticsParams()} />


      {/* Custom Date Range Modal */}
      <Modal
        open={showCustomDateModal}
        onClose={() => {
          setShowCustomDateModal(false);
        }}
        title="Select Custom Date Range"
        primaryAction={{
          content: 'Apply',
          onAction: () => {
            // Convert Date objects to YYYY-MM-DD strings using local timezone
            const startStr = formatDateLocal(selectedDates.start);
            const endStr = formatDateLocal(selectedDates.end);
            setCustomStartDate(startStr);
            setCustomEndDate(endStr);
            setDateRangeMode('custom');
            setShowCustomDateModal(false);
          },
          disabled: !selectedDates.start || !selectedDates.end
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowCustomDateModal(false);
            }
          }
        ]}
      >
        <Modal.Section>
          <Box paddingBlockEnd="400">
            <Text variant="bodySm" tone="subdued">
              Select a start date and end date for your custom range
            </Text>
          </Box>
          <DatePicker
            month={datePickerMonth}
            year={datePickerYear}
            onChange={setSelectedDates}
            onMonthChange={(month, year) => {
              setDatePickerMonth(month);
              setDatePickerYear(year);
            }}
            selected={selectedDates}
            allowRange
          />
        </Modal.Section>
      </Modal>

      {/* Brand-Specific Marketplace Breakdown */}
      <MarketplaceBreakdown
        brandId={brandId}
        period={getAnalyticsParams().days || parseInt(analyticsFilter)}
        dateRange={getAnalyticsParams().startDate && getAnalyticsParams().endDate ? {
          startDate: getAnalyticsParams().startDate,
          endDate: getAnalyticsParams().endDate
        } : null}
        title="Marketplace Performance"
        onMarketplaceSelect={(marketplace) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('Selected marketplace for brand:', marketplace, brand.name);
          }
        }}
      />
    </BlockStack>
  );

  const renderOrders = () => {
    // Filter options.
    // Must cover the server's whole accepted set - orders.controller.js:139 and :700 validate
    // `statusFilter` against VALID_STATUSES = ['FULFILLED','PAID','REFUNDED',
    // 'PARTIALLY_REFUNDED','PENDING','AUTHORIZED','VOIDED'] and silently DROP anything else.
    // AUTHORIZED and VOIDED were missing here, so orders in those states existed in the table
    // but could never be filtered for.
    const ordersStatusOptions = ORDER_STATUS_OPTIONS;

    const ordersFilters = [
      {
        key: 'shippingFeeReview',
        label: 'Shipping fee',
        filter: (
          <ChoiceList
            title="Shipping fee"
            titleHidden
            choices={[{ label: 'Needs review', value: 'needs-review' }]}
            selected={ordersShippingReviewFilter}
            onChange={setOrdersShippingReviewFilter}
            allowMultiple
          />
        ),
        shortcut: true,
      },
      {
        key: 'status',
        label: 'Status',
        filter: (
          <ChoiceList
            title="Status"
            titleHidden
            choices={ordersStatusOptions}
            selected={ordersStatusFilter}
            onChange={setOrdersStatusFilter}
            allowMultiple
          />
        ),
        shortcut: true,
      },
      {
        key: 'marketplace',
        label: 'Marketplace',
        filter: (
          <ChoiceList
            title="Marketplace"
            titleHidden
            choices={ordersMarketplaceOptions}
            selected={ordersMarketplaceFilter}
            onChange={setOrdersMarketplaceFilter}
            allowMultiple
          />
        ),
        shortcut: true,
      },
      {
        key: 'amount',
        label: 'Amount Range',
        filter: (
          <RangeSlider
            label="Net Amount Range"
            value={ordersAmountRange}
            onChange={setOrdersAmountRange}
            output
            min={0}
            max={5000}
            step={50}
            prefix="$"
          />
        ),
      },
      {
        key: 'dateRange',
        label: 'Date Range',
        filter: (
          <BlockStack gap="300">
            <TextField
              label="Start Date"
              type="date"
              value={ordersStartDate}
              onChange={setOrdersStartDate}
              autoComplete="off"
            />
            <TextField
              label="End Date"
              type="date"
              value={ordersEndDate}
              onChange={setOrdersEndDate}
              autoComplete="off"
              min={ordersStartDate}
            />
            <Button
              size="slim"
              onClick={() => {
                const today = new Date();
                const thirtyDaysAgo = new Date(today);
                thirtyDaysAgo.setDate(today.getDate() - 30);
                setOrdersStartDate(formatDateLocal(thirtyDaysAgo));
                setOrdersEndDate(formatDateLocal(today));
              }}
            >
              Last 30 days
            </Button>
          </BlockStack>
        ),
      },
    ];

    // Headings, sortable flags and toggle labels all come off BRAND_ORDER_COLUMNS, so
    // they cannot disagree about which column sits at which index. See that array.
    const ordersHeadings = BRAND_ORDER_HEADINGS;

    const ordersEmptyStateMarkup = (
      <EmptyState
        heading="No orders found"
        content={
          ordersAppliedFilters.length > 0
            ? "Try adjusting your search or filter criteria to find orders."
            : "Orders will appear here once they're synced from this brand."
        }
        image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
        action={ordersAppliedFilters.length > 0 ? {
          content: 'Clear filters',
          onAction: handleOrdersFiltersClearAll,
        } : undefined}
      />
    );

    if (ordersError) {
      return (
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to load orders. {ordersError.message || 'Please try refreshing the page.'}</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      );
    }

    return (
      <>
      <Layout>
        {/* Sync & Recalculate, above the table whose commission columns it rewrites. */}
        {renderOrderSyncSection()}

        {/* Resync Banner */}
        {resyncBanner && (
          <Layout.Section>
            <Banner
              tone={resyncBanner.tone}
              onDismiss={() => setResyncBanner(null)}
            >
              {resyncBanner.message}
            </Banner>
          </Layout.Section>
        )}

        {/* Orders Table */}
        <Layout.Section>
          <Card>
            {/* Filters integrated into table card */}
            <Box padding="200">
              <Filters
                queryValue={ordersSearchQuery}
                queryPlaceholder="Search orders..."
                onQueryChange={setOrdersSearchQuery}
                onQueryClear={() => setOrdersSearchQuery('')}
                filters={ordersFilters}
                appliedFilters={ordersAppliedFilters}
                onClearAll={handleOrdersFiltersClearAll}
                disabled={ordersLoading}
              />
            </Box>
            <Divider />
            <IndexTable
              resourceName={ordersResourceName}
              // The API's order total, not this page's row array (10 orders inflated by
              // injected refund rows and the summary row). pagination.totalOrders was already
              // in hand - only totalPages was being read off ordersPagination.
              // 1 when there is nothing, so IndexTable still renders the header row: at
              // itemCount 0 it swaps the whole table for emptyState, headings included,
              // which is why that prop is gone. The empty state itself moved inside.
              itemCount={ordersRowMarkup.length ? (ordersTotalOrders || ordersRowMarkup.length) : 1}
              headings={ordersHeadings}
              sortToggleLabels={BRAND_ORDER_SORT_TOGGLE_LABELS}
              loading={ordersLoading}
              selectable={false}
              sortable={BRAND_ORDER_SORTABLE}
              sortDirection={ordersSortDirection}
              sortColumnIndex={brandOrderSortColumnIndex(ordersSortBy)}
              onSort={handleOrdersSort}
              hasZebraStriping={true}
            >
              {ordersRowMarkup.length === 0 ? (
                <IndexTable.Row id="empty-brand-orders" position={0}>
                  <IndexTable.Cell colSpan={ordersHeadings.length}>
                    {ordersEmptyStateMarkup}
                  </IndexTable.Cell>
                </IndexTable.Row>
              ) : ordersRowMarkup}
            </IndexTable>

            {/* Table Footer with Summary and Pagination */}
            {(!ordersLoading && orders.length > 0) && (
              <>
                <Divider />
                <Box padding="400">
                  <InlineStack gap="800" align="space-between" blockAlign="center">

                    {/* Rows per page. Kept outside the pager's own condition: the pager is
                        dropped when every order fits on one page, but the row count is how
                        someone GETS a second page, so hiding it at size 50 would strand
                        them there. */}
                    <PageSizeSelect
                      value={ordersItemsPerPage}
                      onChange={handleOrdersItemsPerPageChange}
                    />

                    {/* Pagination */}
                    {ordersTotalPages > 1 && (
                      <Box style={{ flexShrink: 0 }}>
                        <InlineStack gap="200" blockAlign="center">
                          <Button
                            disabled={ordersCurrentPage <= 1}
                            onClick={handleOrdersPreviousPage}
                            size="slim"
                          >
                            Previous
                          </Button>
                          <Text variant="bodySm" tone="subdued">
                            {ordersCurrentPage} of {ordersTotalPages}
                          </Text>
                          <Button
                            disabled={ordersCurrentPage >= ordersTotalPages}
                            onClick={handleOrdersNextPage}
                            size="slim"
                          >
                            Next
                          </Button>
                        </InlineStack>
                      </Box>
                    )}
                  </InlineStack>
                </Box>
              </>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* OUTSIDE the Layout, which takes Layout.Section children and lays them out as
          columns — a bare element among them is not a section and does not belong in that
          grid. The global Orders table mounts this as a sibling of its own page content for
          the same reason.

          The same panel that table opens, so one order does not read two different ways
          depending on which screen an admin reached it from. The brand is attached here
          because these rows carry no brand of their own — without it the panel's "View in
          Shopify" footer has no domain to link to. */}
      <OrderDetailPanel
        order={selectedOrderId
          ? (() => {
            const row = orders.find((o) => o.id === selectedOrderId);
            return row && { ...row, brand: { name: brand?.name, shopDomain: brand?.shopDomain } };
          })()
          : null}
        open={orderDetailOpen}
        onClose={() => setOrderDetailOpen(false)}
      />
    </>
    );
  };

  /**
   * Activity Log — a table and nothing else.
   *
   * No heading and no description: the tab says Activity Log, and the columns (Type, Action,
   * Details, User, Date) say what is logged more precisely than the old sentence, which promised
   * "commission and shipping settings" while the rows also carry invoice and return-fee entries.
   *
   * `padding="0"` on the Card, so the table is flush. With the prose gone, Card's own 16px sat
   * as a band above the header row with nothing in it. A table filling its card is Polaris' own
   * pattern; the header row's own cell padding gives it the breathing room it needs. The empty
   * state keeps a padded Box, since EmptyState against a bare card edge has no margin at all.
   */
  const renderActivityLog = () => {
    /**
     * The page on screen, CLAMPED: the log is replaced wholesale whenever the brand is
     * re-read, so it can shrink under a page that was valid when it was chosen, and an
     * out-of-range slice would render an empty table where entries still exist.
     */
    const activityTotalPages = Math.max(1, Math.ceil(activityLog.length / activityPageSize));
    const activitySafePage = Math.min(activityPage, activityTotalPages);
    const visibleActivity = activityLog.slice(
      (activitySafePage - 1) * activityPageSize,
      activitySafePage * activityPageSize,
    );

    return (
    <Card padding="0">
        {/* The table renders with no entries in it.

            An empty log used to swap the table for an EmptyState, so a brand with nothing
            logged yet showed no columns and therefore no statement of WHAT is logged — which
            is the whole point of the column list, per the note above. DataTable takes no
            `emptyState`, but it derives a cell's colSpan from the row's length against
            headings.length (DataTable.js getColSpan), so a one-cell row spans the table. The
            EmptyState is that row, wording unchanged, still in its padded Box because
            EmptyState against a bare card edge has no margin at all. */}
        <DataTable
          columnContentTypes={['text', 'text', 'text', 'text', 'text']}
          headings={['Type', 'Action', 'Details', 'User', 'Date'].map((h) => (
            <Text key={h} variant="bodyMd" fontWeight="semibold" as="span">{h}</Text>
          ))}
          rows={activityLog.length === 0 ? [[
            <Box key="no-activity" padding="400">
              <EmptyState
                heading="No activity recorded"
                content="Configuration changes and updates will appear here for audit purposes."
              />
            </Box>,
          ]] : visibleActivity.map(activity => [
            <Badge tone={activity.type === 'commission' ? 'info' : 'success'}>
              {activity.type}
            </Badge>,
            activity.action,
            // Truncated, with the whole thing on hover.
            //
            // DataTable sizes a cell to its content, so one long entry — a bulk shipping
            // update lists every changed rate — stretched the row and pushed User and Date
            // off the visible area, with the text simply stopping where the table ran out
            // of room and no indication anything was missing. maxWidth plus Text truncate
            // gives a real ellipsis, and the Tooltip means nothing is actually lost.
            (() => {
              const text = formatActivityDetails(activity.action, activity.details);
              if (!text) return '—';
              return (
                <div style={{ maxWidth: '420px' }}>
                  <Tooltip content={text} preferredPosition="above">
                    <Text as="span" truncate>{text}</Text>
                  </Tooltip>
                </div>
              );
            })(),
            activity.user,
            (() => {
              const date = new Date(activity.timestamp);
              return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleString();
            })()
          ])}
        />
        {/* Both controls in one row, below the flush table. The pager drops out when the
            whole log fits; the row count stays, since it is how someone gets a second
            page at all. Labelled with the page the rows were actually sliced with. Neither
            appears over an empty log: there is no page to size. */}
        {activityLog.length > 0 && (
          <Box padding="400" borderBlockStartWidth="025" borderColor="border">
            <InlineStack align="space-between" blockAlign="center">
              <PageSizeSelect
                value={activityPageSize}
                onChange={(next) => { setActivityPageSize(next); setActivityPage(1); }}
              />
              {activityTotalPages > 1 && (
                <Pagination
                  hasPrevious={activitySafePage > 1}
                  onPrevious={() => setActivityPage(activitySafePage - 1)}
                  hasNext={activitySafePage < activityTotalPages}
                  onNext={() => setActivityPage(activitySafePage + 1)}
                  label={`Page ${activitySafePage} of ${activityTotalPages}`}
                />
              )}
            </InlineStack>
          </Box>
        )}
    </Card>
    );
  };

  // No page title or subtitle: the wireframe goes straight to the header card, and a
  // Page title here repeated the brand name and domain that the card states directly
  // below it.
  //
  // backAction is gone too — with no title for it to sit beside, Polaris rendered it alone
  // in an empty band above the card. The same control now lives inside the header card,
  // first in the row. One back affordance, not two.
  return (
    <Page fullWidth>
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError('')}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {/* Sync In Progress Global Banner */}
        {isSyncing && (
          <Layout.Section>
            <Banner tone="info" title="Sync In Progress">
              <BlockStack gap="200">
                <Text>
                  Order data synchronization is currently running. Some configuration options are temporarily disabled.
                </Text>
                <Text variant="bodySm" tone="subdued">
                  You can monitor progress in the Data Sync tab. The sync will complete automatically.
                </Text>
              </BlockStack>
            </Banner>
          </Layout.Section>
        )}

        {/* Brand Header */}
        <Layout.Section>
          <Card>
            {/* No padding on this Box — Card already applies padding {xs:'400'}
                (node_modules/@shopify/polaris/…/Card.js:10), so setting 400 here as well
                rendered 32px and the wireframe asks for 16. That doubled inset was most of
                this card's height. The Box stays only because it wraps the row. */}
            <Box>
              {/* No back control here, deliberately.

                  There is no Brands index page — `/` is the Dashboard, and the brand list is
                  in the left sidebar, which is always on screen. So a back link could only be
                  labelled for a destination that does not exist ("Brands", pointing at the
                  Dashboard) or labelled honestly ("Dashboard") and duplicate a sidebar item
                  two inches away. Page's backAction had the same problem and additionally
                  floated in an empty band above the card. */}
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="300" blockAlign="center">
                  {/* 8px around a 20px icon, not 16 — a 52px tile next to two lines of text
                      that occupy 48px was setting the card's height by itself, and the status
                      colour reads just as clearly at 36px. */}
                  <Box
                    background={brand.isActive ? "bg-fill-success-secondary" : "bg-fill-critical-secondary"}
                    padding="200"
                    borderRadius="200"
                  >
                    <Icon source={StoreIcon} tone={brand.isActive ? "success" : "critical"} />
                  </Box>
                  <BlockStack gap="100">
                    {/* 16px/650 in the wireframe — headingMd, not headingLg (20px). */}
                    <Text variant="headingMd" as="h1">{brand.name}</Text>
                    <InlineStack gap="300" align="start">
                      <Text variant="bodyMd" tone="subdued">{brand.shopDomain}</Text>
                      <Badge tone={brand.isActive ? 'success' : 'critical'}>
                        {brand.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge tone={brand.approved ? 'success' : 'attention'}>
                        {brand.approved ? 'Approved' : 'Pending Approval'}
                      </Badge>
                    </InlineStack>
                  </BlockStack>
                </InlineStack>

                <InlineStack gap="300">
                  <Button
                    variant="tertiary"
                    icon={RefreshIcon}
                    onClick={fetchBrandData}
                    loading={loading}
                    disabled={isSyncing}
                  >
                    Refresh
                  </Button>
                  <Text variant="bodySm" tone="subdued">
                    Last updated: {(() => {
                      const date = new Date(activityLog[0]?.timestamp || brand.updatedAt);
                      return isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
                    })()}
                  </Text>
                </InlineStack>
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Configuration Tabs */}
        <Layout.Section>
          <Tabs
            tabs={tabs}
            selected={selectedTab}
            onSelect={isSyncing ? undefined : setSelectedTab}
          >
            {/* Less leading space on Analytics.
            
                Every other tab opens with a Card, so 24px separates the tab strip from that
                card's top edge. Analytics opens with the bare period selector — a right-aligned
                button on an otherwise empty row — so the 24px sat above a row that is itself
                mostly empty and read as a void between the tabs and the figures. The filter row
                supplies its own separation. */}
            <Box paddingBlockStart={selectedTab === tabIndex('analytics') ? '200' : '600'}>
              {selectedTab === tabIndex('settings') && (
                /* The wireframe grids ONLY the first row.
                   `.grid-2` opens at artifact line 2586 and closes at 2668, wrapping Brand
                   Status and Order Sources to Bill alone; Commission Rates (2671) and Invoice
                   Settings (2705) sit OUTSIDE it as full-width siblings. This used to wrap all
                   four children in one grid — an earlier reading of "four cards two across" —
                   which squeezed the two widest cards into half-width columns and is why the
                   commission rate fields and the invoice form looked cramped. */
                <BlockStack gap="400">
                  <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                  {/* Brand Status & Module Flags */}
                  <Card>
                    {/* No padding — Card already applies 400. This was 400 as well, so the
                        card carried a 32px inset while Order Sources beside it carried 40
                        (Card 400 + Box 600). Both now sit on Card's own 16px, which is why
                        they are shorter AND finally the same shape. */}
                    <Box padding="200">
                      <BlockStack gap="300">
                        {/* Save in the header, matching every other card in this tab. */}
                        <InlineStack align="space-between" blockAlign="center" gap="400" wrap={false}>
                          <Text variant="headingMd" as="h3">Brand Status</Text>
                          <Button
                            variant="primary"
                            onClick={saveBrandFlags}
                            loading={saving}
                            disabled={saving || !flagsDirty}
                          >
                            Save
                          </Button>
                        </InlineStack>

                        {/* Approved is the GATE, not a module — unchecked, the brand sees the
                            access page and nothing else — so it sits on its own above a divider
                            rather than in the grid with the four module flags.
                            
                            These were one `InlineStack wrap` of five checkboxes with different
                            label widths, one of them carrying helpText and so taller than the
                            rest. Wrapping produced ragged rows and a block of dead space under
                            them. A fixed two-column grid puts every checkbox on a predictable
                            line. */}
                        <Checkbox
                          label="Approved"
                          helpText="Brand can access the app when approved"
                          checked={flagForm.approved}
                          onChange={(checked) => setFlagForm((f) => ({ ...f, approved: checked }))}
                          disabled={saving}
                        />

                        <Divider />

                        <BlockStack gap="300">
                          <Text variant="bodySm" tone="subdued">Modules</Text>
                          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
                            <Checkbox
                              label="Billing Enabled"
                              checked={flagForm.billingEnabled}
                              onChange={(checked) => setFlagForm((f) => ({ ...f, billingEnabled: checked }))}
                              disabled={saving}
                            />
                            <Checkbox
                              label="Reporting Enabled"
                              checked={flagForm.reportingEnabled}
                              onChange={(checked) => setFlagForm((f) => ({ ...f, reportingEnabled: checked }))}
                              disabled={saving}
                            />
                            <Checkbox
                              label="Logistics Enabled"
                              checked={flagForm.logisticsEnabled}
                              onChange={(checked) => setFlagForm((f) => ({ ...f, logisticsEnabled: checked }))}
                              disabled={saving}
                            />
                            <Checkbox
                              label="Connector Enabled"
                              checked={flagForm.connectorEnabled}
                              onChange={(checked) => setFlagForm((f) => ({ ...f, connectorEnabled: checked }))}
                              disabled={saving}
                            />
                          </InlineGrid>
                        </BlockStack>
                      </BlockStack>
                    </Box>
                  </Card>
                  <OrderSourceSettings
                    brand={brand}
                    saving={saving}
                    setSaving={setSaving}
                    setError={setError}
                    fetchBrandData={fetchBrandData}
                  />
                  </InlineGrid>
                  {/* Full width from here down, per the wireframe. */}
                  <CommissionSettings
                    saving={saving}
                    setSaving={setSaving}
                    setError={setError}
                    isSyncing={isSyncing}
                    defaultRate={defaultRate}
                    setDefaultRate={setDefaultRate}
                    updateDefaultCommission={updateDefaultCommission}
                    brand={brand}
                    availableMarketplaceTags={availableMarketplaceTags}
                    availableMarketplacesLoading={availableMarketplacesLoading}
                    fetchBrandData={fetchBrandData}
                  />
                  <InvoiceSettings
                    newRecipientEmail={newRecipientEmail}
                    setNewRecipientEmail={setNewRecipientEmail}
                    brandId={brandId}
                    selectedTab={selectedTab}
                    apiClient={apiClient}
                  />
                </BlockStack>
              )}
              {selectedTab === tabIndex('shipping') && renderShippingConfiguration()}
              {selectedTab === tabIndex('catalogue') && (
                <ShopifyCatalogueTab brandId={brandId} brandName={brand?.name} shopDomain={brand?.shopDomain} />
              )}
              {selectedTab === tabIndex('analytics') && renderAnalytics()}
              {selectedTab === tabIndex('orders') && renderOrders()}
              {selectedTab === tabIndex('adjustments') && (
                <CommissionAdjustmentsTable
                  brandId={brandId}
                  showBrandColumn={false}
                  title="Commission Adjustments"
                  subtitle="Manual commission adjustments for this brand"
                  embedded={true}
                />
              )}
              {selectedTab === tabIndex('activity') && renderActivityLog()}
              {/* Queue and Sync History, moved out of Retail Connect and given tabs of their
                  own. They were two cards at the bottom of a long tab, which is where work
                  that has failed is least likely to be seen — and they show BILLING runs as
                  well as connector ones, so they were also under a heading that named the
                  wrong half of what they contain. */}
              {selectedTab === tabIndex('queue') && (
                <Card>
                  <Box padding="400">
                    <BlockStack gap="300">
                      <BlockStack gap="050">
                        <Text variant="headingMd" as="h2">Queue &mdash; live</Text>
                        <Text variant="bodySm" tone="subdued">
                          Work queued or running right now for this brand, plus the last
                          couple of hours &mdash; connector and billing together. Includes runs
                          on this brand&apos;s retailer accounts, which cover every brand on
                          that account rather than this one alone. A job switched off in Sync
                          Settings appears here and nowhere else.
                        </Text>
                      </BlockStack>
                      <QueueTable
                        brandId={brandId}
                        marketplaceOptions={runRetailerOptions}
                      />
                    </BlockStack>
                  </Box>
                </Card>
              )}

              {selectedTab === tabIndex('history') && (
                <Card>
                  <Box padding="400">
                    <BlockStack gap="300">
                      {/* Heading only. The paragraph that was here described the table the
                          reader is already looking at — and every claim in it is now made by
                          the table itself: the Source column says which system, the status
                          badge explains itself on hover, and the eye opens the whole record. */}
                      <Text variant="headingMd" as="h2">Sync History</Text>
                      <SyncHistoryTable
                        brandId={brandId}
                        marketplaces={runRetailerOptions.map((o) => ({ id: o.value, name: o.label }))}
                      />
                    </BlockStack>
                  </Box>
                </Card>
              )}

              {selectedTab === tabIndex('connector') && brand?.connectorEnabled && (
                <ConnectorTab brandId={brandId} brandName={brand?.name} />
              )}
            </Box>
          </Tabs>
        </Layout.Section>
      </Layout>

      {/* Return Fee Override Modal */}
      <Modal
        open={showReturnFeeModal}
        onClose={() => {
          setShowReturnFeeModal(false);
          setEditingMarketplace('');
          setNewReturnFee('0.00');
        }}
        title="Add Marketplace Return Fee Override"
        primaryAction={{
          content: 'Add Override',
          onAction: () => {
            if (editingMarketplace && !isNaN(parseFloat(newReturnFee))) {
              saveMarketplaceReturnFee(editingMarketplace, newReturnFee);
            }
          },
          loading: saving,
          disabled: !editingMarketplace || !newReturnFee.trim()
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => {
            setShowReturnFeeModal(false);
            setEditingMarketplace('');
            setNewReturnFee('0.00');
          }
        }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text tone="subdued" variant="bodyMd">
              {hasDefaultReturnFee
                ? (returnFeesUnavailable || defaultReturnFee === ''
                    ? 'Override the default return fee for a specific marketplace. The current default could not be loaded.'
                    : `Override the default return fee ($${parseFloat(defaultReturnFee).toFixed(2)}) for a specific marketplace.`)
                : 'Set a marketplace-specific return fee override. No default return fee is currently configured.'}
            </Text>
            <FormLayout>
              <Select
                label="Marketplace"
                options={[
                  { label: 'Select marketplace...', value: '' },
                  ...availableMarketplaceTags
                    .filter(marketplace => !returnFeeOverrideKeys.includes(marketplace))
                    .map(marketplace => ({
                      label: marketplace,
                      value: marketplace
                    }))
                ]}
                value={editingMarketplace}
                onChange={setEditingMarketplace}
                helpText={(() => {
                  // availableMarketplacesLoading, not marketplacesLoading. This select is fed
                  // by `availableMarketplaceTags` (its own effect, its own flag);
                  // marketplacesLoading belongs to useMarketplacesList(brandId), a different
                  // endpoint whose result only feeds the orders filter. The "Loading available
                  // marketplaces..." text and the disabled state never tracked the fetch they
                  // claimed to.
                  if (availableMarketplacesLoading) return "Loading available marketplaces...";

                  const unconfiguredMarketplaces = availableMarketplaceTags.filter(
                    marketplace => !returnFeeOverrideKeys.includes(marketplace)
                  );

                  if (unconfiguredMarketplaces.length > 0) {
                    return "Choose a marketplace for this return fee";
                  } else if (availableMarketplaceTags.length > 0) {
                    return "All available marketplaces already have brand-level return fees configured";
                  } else {
                    return "No marketplaces detected for this brand";
                  }
                })()}
                disabled={(() => {
                  if (availableMarketplacesLoading) return true;

                  const unconfiguredMarketplaces = availableMarketplaceTags.filter(
                    marketplace => !returnFeeOverrideKeys.includes(marketplace)
                  );

                  return unconfiguredMarketplaces.length === 0;
                })()}
              />
              <TextField
                label="Return Fee"
                type="number"
                value={newReturnFee}
                onChange={setNewReturnFee}
                prefix="$"
                min="0"
                step="0.01"
                helpText="Fee charged for processing returns from this marketplace"
              />
            </FormLayout>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Reset Default Return Fee Modal */}
      <Modal
        open={showResetDefaultFeeModal}
        onClose={() => setShowResetDefaultFeeModal(false)}
        title="Reset Default Return Fee"
        primaryAction={{
          content: 'Reset to Default',
          destructive: true,
          loading: resettingFees,
          onAction: handleResetDefaultFee
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowResetDefaultFeeModal(false)
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text>
              Are you sure you want to reset the default return fee to the global default?
            </Text>
            <Text tone="subdued">
              This will remove the brand-specific override and use the global default of{' '}
              <strong>${globalReturnFeeDefaults.default?.toFixed(2) || '0.00'}</strong>.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Reset All Marketplace Return Fees Modal */}
      <Modal
        open={showResetMarketplaceFeesModal}
        onClose={() => setShowResetMarketplaceFeesModal(false)}
        title="Reset All Marketplace Return Fees"
        primaryAction={{
          content: 'Reset All to Default',
          destructive: true,
          loading: resettingFees,
          onAction: handleResetMarketplaceFees
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowResetMarketplaceFeesModal(false)
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text>
              Are you sure you want to reset all marketplace return fees to their global defaults?
            </Text>
            <Text tone="subdued">
              This will remove{' '}
              <strong>{returnFeeOverrideKeys.filter(key => key !== 'default').length}</strong>{' '}
              brand-specific marketplace override(s) and revert to global defaults.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}